import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarsOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  Table,
  Tag,
  Space,
  Select,
  DatePicker,
  InputNumber,
  Typography,
  Button,
  Modal,
  Divider,
  Alert,
  Radio,
  Tooltip,
  Spin,
  Checkbox,
  Dropdown,
  Popover,
  message,
  Empty,
  theme,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import DateHelper from "app/helpers/dateHelper";
import { SaveState } from "app/shared/ui/GenericSaveAndBackButton";
import { FormError } from "app/shared/model/Form";
import { PRICELIST_VALIDATION_MESSAGES } from "../domain/validation/pricelistErrorMessages";
import { PRICE_MAJOR_MINOR_UNIT_TOOLTIP } from "../domain/tables/priceColumnTooltips";
import { UnitType } from "app/shared/model";
import {
  EditPricelistResponse,
  OfferingType as EditOfferingType,
  FeeOfferingDto,
  ApplicableProduct,
  ApplicableSelectionMode,
  FeeOfferingType,
  SchoolOfferingStatus,
} from "app/shared/model/EditPricelist";
import {
  getCentres,
  getDraftPricelist,
  getFormErrors,
  getIsLoading,
  getSelectedCentre,
  getSelectedPricingYear,
  getPricingYears,
} from "../store/selectors";
import {
  setFormErrors,
  setFormState,
  updateDraftPricelistItem,
  loadDraftPricelist,
} from "../store/actions";
import { ThunkDispatch } from "redux-thunk";
import { Action } from "../store/actionTypes";
import * as api from "app/shared/api";

import { getDivisionId } from "../domain/storage/localStorage";
import { isFeeOffering as isFeeOfferingGuard } from "../domain/offering/guards";
import { normalizeFeeOfferingType } from "../domain/offering/fee";
import {
  PRODUCT_TYPE_NAME_TO_ID,
  PRODUCT_TYPE_ID_TO_NAME,
  PRODUCT_TYPE_LABELS,
  ProductTypeKey,
} from "../domain/offering/productType";
import { PP_COL_W } from "../domain/tables/ppColumnWidths";
import { OpenProductLink } from "app/shared/products/OpenProductLink";
import { SelectAllOnFocusInput } from "app/shared/components/Form/FormField";
import {
  getPricingYearWindow,
  maxIsoDate, minIsoDate,
  shiftPickerDateToEarliestAllowed
} from "app/pages/PricelistNew/domain/pricingYear/window";

const { Text } = Typography;

const SELECTION_MODE_LABELS: Record<ApplicableSelectionMode, string> = {
  SpecificProductType: "All products",
  IncludeSelected: "Include",
  ExcludeSelected: "Exclude",
};

const SELECTION_MODES: ApplicableSelectionMode[] = [
  "SpecificProductType",
  "IncludeSelected",
  "ExcludeSelected",
];

const isFeeOffering = isFeeOfferingGuard;
const DEFAULT_SELECTION_MODE: ApplicableSelectionMode = "IncludeSelected";
const CHECKBOX_ROW_HEIGHT = 24;
const CHECKBOX_ROW_GAP = 8;

const CheckboxSpacer = () => (
    <div style={{ height: CHECKBOX_ROW_HEIGHT }} />
);

const FEE_DATE_INPUT_STYLE = { width: 150 };

const feeFieldErrorOutline = (hasError: boolean): React.CSSProperties | undefined =>
  hasError
    ? { outline: "1px solid #ff4d4f", borderRadius: 6, padding: 4 }
    : undefined;
const FEE_SMALL_INPUT_STYLE = { width: 86 };
const FEE_PRICE_INPUT_STYLE = { width: 110 };

const selectAllOnPriceFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  const el = e.target;
  el.addEventListener("mouseup", (ev) => ev.preventDefault(), { once: true });
  requestAnimationFrame(() => {
    if (document.activeElement === el) el.select();
  });
};
const TOP_ALIGNED_CELL_STYLE: React.CSSProperties = {
  verticalAlign: "middle",
};

const FEE_EDIT_OFFSET_STACK_STYLE: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: CHECKBOX_ROW_GAP,
};

const FEE_LEFT_STACK_STYLE: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: CHECKBOX_ROW_GAP,
};

const FEE_UNITS_STACK_STYLE: React.CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: CHECKBOX_ROW_GAP,
};

const MAX_UNITS_CHECKBOX_ROW_STYLE: React.CSSProperties = {
  width: "100%",
  minHeight: CHECKBOX_ROW_HEIGHT,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
};

const MAX_UNITS_SPACER_STYLE: React.CSSProperties = {
  width: "100%",
  height: CHECKBOX_ROW_HEIGHT,
};

const DATE_PICKER_SELECT_ALL_COMPONENTS = { input: SelectAllOnFocusInput };

const normalizeSelectionMode = (
    value: string | null | undefined
): ApplicableSelectionMode => {
  const v = (value ?? "").trim().toLowerCase();

  if (v === "excludeselected" || v === "exclude") return "ExcludeSelected";
  if (v === "includeselected" || v === "include") return "IncludeSelected";
  if (v === "specificproducttype" || v === "specific")
    return "SpecificProductType";

  return DEFAULT_SELECTION_MODE;
};

const iso = (d: Dayjs | null): string => d ? d.startOf("day").format("YYYY-MM-DD") : "";

const isFixedPriceUnitTypeId = (unitTypeId: number): boolean => Number(unitTypeId) === 2;

const deriveFeeOfferingTypeFromUnitTypeId = (unitTypeId: number
): FeeOfferingType => isFixedPriceUnitTypeId(unitTypeId) ? "One Off" : "Supplement";

const createSupplementCurrencyPrice = (currencyIsoCode: string) => ({
  currencyIsoCode,
  pricePerMajorUnit: 0,
  pricePerMinorUnit: 0,
});

const createEmptySupplementPeriod = (
    year: number,
    enabledCurrencies: string[]
): SupplementPeriodItem => ({
  year,
  startDate: "",
  endDate: "",
  prices: enabledCurrencies.map(createSupplementCurrencyPrice),
});

const getOrCreateOneOffPriceIndex = (
    entry: FeeOfferingDto,
    year: number,
    currencyIsoCode: string
): number => {
  const idx = (entry.oneOffFeePrices ?? []).findIndex(
      (p) =>
          p != null &&
          Number((p as any).year) === Number(year) &&
          String((p as any).currencyIsoCode ?? "").toUpperCase() ===
          String(currencyIsoCode).toUpperCase()
  );
  if (idx !== -1) return idx;

  entry.oneOffFeePrices = [
    ...(entry.oneOffFeePrices ?? []),
    { year, currencyIsoCode, amount: 0 },
  ];
  return entry.oneOffFeePrices.length - 1;
};

type ApplicableTarget = "Types" | "Products";
const DEFAULT_TARGET_FOR_GROUPED: ApplicableTarget = "Types";

const supportsTypesAndProducts = (pt?: ProductTypeKey) => pt === "Accommodation" || pt === "AddOn";

const productTypeIdFromApplicableItem = (item: ApplicableProduct): number => {
  const rawId = Number((item as any)?.productTypeId ?? 0);
  if (Number.isFinite(rawId) && rawId > 0) return rawId;

  const rawName = String(
    (item as any)?.productTypeName ?? (item as any)?.productType ?? ""
  ).trim();
  if (!rawName) return 0;

  const n = rawName.toLowerCase().replace(/[^a-z]/g, "");
  if (n === "course" || n === "courses") return PRODUCT_TYPE_NAME_TO_ID.Course;
  if (n === "accommodation" || n === "accommodations") return PRODUCT_TYPE_NAME_TO_ID.Accommodation;
  if (n === "addon" || n === "addons" || n === "addonproduct" || n === "addonsproduct") return PRODUCT_TYPE_NAME_TO_ID.AddOn;
  if (n === "transfer" || n === "transfers") return PRODUCT_TYPE_NAME_TO_ID.Transfer;
  if (n === "package" || n === "packages") return PRODUCT_TYPE_NAME_TO_ID.Package;
  return 0;
};

const inferTargetFromItem = (ap: ApplicableProduct): ApplicableTarget => {
  const pt = Number(ap.productTypeId ?? 0);

  if (pt === 2) {
    if ((ap.accommodationTypeIds ?? []).length > 0) return "Types";
    if ((ap.productIds ?? []).length > 0) return "Products";
    return DEFAULT_TARGET_FOR_GROUPED;
  }

  if (pt === 3) {
    if ((ap.addOnTypeIds ?? []).length > 0) return "Types";
    if ((ap.productIds ?? []).length > 0) return "Products";
    return DEFAULT_TARGET_FOR_GROUPED;
  }

  return "Products";
};

const getApplicableIds = (
  ap: ApplicableProduct,
  target?: ApplicableTarget
): number[] => {
  const pt = Number(ap.productTypeId ?? 0);

  if (pt === 2) {
    if (target === "Products") return ap.productIds ?? [];
    if (target === "Types") return ap.accommodationTypeIds ?? [];
    return (
      (ap.accommodationTypeIds?.length
        ? ap.accommodationTypeIds
        : ap.productIds) ?? []
    );
  }

  if (pt === 3) {
    if (target === "Products") return ap.productIds ?? [];
    if (target === "Types") return ap.addOnTypeIds ?? [];
    return (ap.addOnTypeIds?.length ? ap.addOnTypeIds : ap.productIds) ?? [];
  }

  return ap.productIds ?? [];
};

const setApplicableIds = (
  ap: ApplicableProduct,
  ids: number[],
  target?: ApplicableTarget
): ApplicableProduct => {
  const pt = Number(ap.productTypeId ?? 0);

  const cleaned: ApplicableProduct = {
    ...ap,
    productIds: [],
    accommodationTypeIds: [],
    addOnTypeIds: [],
  };

  if (pt === 2) {
    if (target === "Products") cleaned.productIds = ids;
    else cleaned.accommodationTypeIds = ids; // default Types
    return cleaned;
  }

  if (pt === 3) {
    if (target === "Products") cleaned.productIds = ids;
    else cleaned.addOnTypeIds = ids; // default Types
    return cleaned;
  }

  cleaned.productIds = ids;
  return cleaned;
};

type UnitRules = { allowsMinor: boolean; minorMax?: number };

const getUnitRules = (unitTypeId: number, unitTypeName?: string): UnitRules => {
  const id = Number(unitTypeId);

  // FixedPrice=2, Days=3, CalendarWeeks=5, WorkingWeeks=6, Months=7
  if (id === 5) return { allowsMinor: true, minorMax: 6 };
  if (id === 6) return { allowsMinor: true, minorMax: 4 };
  if (id === 2 || id === 3 || id === 7) return { allowsMinor: false };

  const n = (unitTypeName ?? "").trim().toLowerCase();
  if (n.includes("working")) return { allowsMinor: true, minorMax: 4 };
  if (n.includes("calendar")) return { allowsMinor: true, minorMax: 6 };
  if (n.includes("week")) return { allowsMinor: true, minorMax: 6 };

  return { allowsMinor: false };
};

const getUnitTypeNameForFee = (
  feeUnitTypeId: number,
  unitTypes: UnitType[],
  fallback?: string
) =>
  unitTypes.find((u) => Number(u.id) === Number(feeUnitTypeId))?.name ??
  fallback;

const sanitizeFeeByUnitType = (
    draft: FeeOfferingDto,
    unitTypes: UnitType[],
    fallbackUnitTypeName?: string
) => {
  const unitTypeName = getUnitTypeNameForFee(
      draft.feeUnitTypeId,
      unitTypes,
      fallbackUnitTypeName
  );
  const offeringType =
    String((draft as any)?.feeOfferingType ?? "").trim()
      ? normalizeFeeOfferingType((draft as any).feeOfferingType)
      : deriveFeeOfferingTypeFromUnitTypeId(draft.feeUnitTypeId);
  const rules = getUnitRules(draft.feeUnitTypeId, unitTypeName);

  draft.feeOfferingType = offeringType;
  draft.supplementPeriods = draft.supplementPeriods ?? [];
  draft.oneOffFeePrices = draft.oneOffFeePrices ?? [];
  draft.applicableProducts = draft.applicableProducts ?? [];

  if (offeringType === "One Off") {
    draft.supplementHasSpecificDates = false;
    draft.supplementPeriods = [];
    draft.supplementMaximumUnits = {
      hasMaximum: false,
      majorUnits: 0,
      minorUnits: 0,
    };
  } else {
    draft.oneOffFeePrices = [];
    draft.supplementMaximumUnits ??= {
      hasMaximum: false,
      majorUnits: 0,
      minorUnits: 0,
    };

    if (!rules.allowsMinor) {
      draft.supplementMaximumUnits.minorUnits = 0;
      (draft.supplementPeriods ?? []).forEach((period) => {
        period.prices = (period.prices ?? []).map((p: any) => ({
          ...p,
          pricePerMinorUnit: 0,
        }));
      });
    } else {
      (draft.supplementPeriods ?? []).forEach((period) => {
        period.prices = (period.prices ?? []).map((p: any) => ({
          ...p,
          pricePerMinorUnit: Number((p as any).pricePerMinorUnit ?? 0),
        }));
      });
    }
  }
};

const toIso = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  return String(value);
};

const normalizeCurrencyIso = (value: unknown): string =>
    String(value ?? "").trim().toUpperCase();

const buildAllDatesPeriodFromSpecific = (
    periods: SupplementPeriodItem[] | undefined,
    selectedYear: number,
    enabledCurrencies: string[]
): SupplementPeriodItem => {
  const source = Array.isArray(periods) ? periods : [];

  const firstPeriodWithPrices =
      source.find((p) => Array.isArray(p?.prices) && p.prices.length > 0) ?? null;

  const pricesFromFirst = Array.isArray(firstPeriodWithPrices?.prices)
      ? firstPeriodWithPrices!.prices
      : [];

  const byCurrency = new Map<string, { currencyIsoCode: string; pricePerMajorUnit: number; pricePerMinorUnit: number }>();

  for (const price of pricesFromFirst) {
    const iso = normalizeCurrencyIso((price as any)?.currencyIsoCode);
    if (!iso) continue;

    byCurrency.set(iso, {
      currencyIsoCode: iso,
      pricePerMajorUnit: Number((price as any)?.pricePerMajorUnit ?? 0),
      pricePerMinorUnit: Number((price as any)?.pricePerMinorUnit ?? 0),
    });
  }

  for (const isoRaw of enabledCurrencies) {
    const iso = normalizeCurrencyIso(isoRaw);
    if (!iso) continue;

    if (!byCurrency.has(iso)) {
      byCurrency.set(iso, {
        currencyIsoCode: iso,
        pricePerMajorUnit: 0,
        pricePerMinorUnit: 0,
      });
    }
  }

  return {
    year: Number(selectedYear),
    startDate: null,
    endDate: null,
    prices: Array.from(byCurrency.values()).sort((a, b) =>
        a.currencyIsoCode.localeCompare(b.currencyIsoCode)
    ),
  };
};

const getEmptyApplicableEditorState = () => ({
  selectedProductType: undefined as ProductTypeKey | undefined,
  selectionMode: undefined as ApplicableSelectionMode | undefined,
  selectionTarget: undefined as ApplicableTarget | undefined,
  selectedProductIds: [] as string[],
  editingTypeId: null as number | null,
  error: undefined as string | undefined,
});

const getApplicableDurationState = (
    row: FeeRow,
    items: ApplicableProduct[]
) => {
  if (row.offeringType !== "One Off") {
    return {
      minDuration: null,
      maxDuration: null,
    };
  }

  const firstWithDuration = items.find(
      (x) => x.minDuration != null || x.maxDuration != null
  );

  return {
    minDuration:
        firstWithDuration?.minDuration != null
            ? Number(firstWithDuration.minDuration)
            : null,
    maxDuration:
        firstWithDuration?.maxDuration != null
            ? Number(firstWithDuration.maxDuration)
            : null,
  };
};

const fmtMoney = (n: any) => Number(n ?? 0).toFixed(2);
const fmtDate = (s?: Date | string | null) =>
    s ? dayjs(s).format("DD-MMM-YYYY") : "—";

const getSchoolId = (value: any): number | null => {
  const raw = Number(value?.schoolId?.id ?? value?.schoolId ?? value?.id ?? value);
  return Number.isFinite(raw) ? raw : null;
};

const getSchoolName = (value: any, fallbackIndex?: number): string => {
  const explicit =
      value?.schoolName ??
      value?.name ??
      value?.school?.name ??
      value?.schoolId?.name ??
      value?.schoolId?.text;
  if (explicit) return String(explicit);

  const id = getSchoolId(value);
  if (id != null) return `School ${id}`;
  return `School ${fallbackIndex ?? 0}`;
};

const renderSchoolStatusTag = (status: SchoolOfferingStatus) => {
  const cls =
      status === "Active"
          ? "school-status-tag school-status-active"
          : status === "Inactive"
              ? "school-status-tag school-status-inactive"
              : status === "ScheduledInactive"
                  ? "school-status-tag school-status-scheduled"
                  : "school-status-tag school-status-missing";
  return <Tag className={cls}>{status}</Tag>;
};

type SupplementPeriodItem = NonNullable<FeeOfferingDto["supplementPeriods"]>[number];

export interface FeePricelistTableHandle {
  beginAdd: () => void;
}

interface FeeRow {
  key: string;
  index: number;
  entry: FeeOfferingDto;
  unitTypeName?: string;
  offeringType: FeeOfferingType;
}

interface FeeFlatRow {
  key: string;
  feeRow: FeeRow;
  periodIndex: number | null;
  period: SupplementPeriodItem | null;
  feeRowSpan: number;
  isFirstPeriodRow: boolean;
}

interface OwnProps {
  editMode: boolean;
  selectedSchoolId?: number;
  enabledCurrencies: string[];
  showInactiveOnly?: boolean;
  centreSchools: { id: number; name: string }[];
  allowedStartDate?: string;
  allowedEndDate?: string;
  rowHighlightColors?: Record<string, string>;
  onRowHighlightChange?: (offeringKey: string, color: string | null) => void;
  reloadPricelist: () => void;
}

type ApplicableProductsModalState = {
  visible: boolean;
  row: FeeRow | null;
  items: ApplicableProduct[];
  selectedProductType?: ProductTypeKey;
  selectionMode?: ApplicableSelectionMode;
  selectedProductIds: string[];
  editingTypeId: number | null;
  selectionTarget?: ApplicableTarget;
  minDuration: number | null;
  maxDuration: number | null;
  error?: string;
  hadItems: boolean;
};

const FeePricelistTable = React.forwardRef<FeePricelistTableHandle, OwnProps>(
(props, ref) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const draftPricelist = useSelector(getDraftPricelist);
  const formErrors = useSelector(getFormErrors);
  const isLoading = useSelector(getIsLoading);
  const selectedCentreCode = useSelector(getSelectedCentre);
  const selectedPricingYear = useSelector(getSelectedPricingYear);
  const centres = useSelector(getCentres);
  const pricingYears = useSelector(getPricingYears);

  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false);
  const [availableFees, setAvailableFees] = useState<
      { id: number; name: string; unitTypeId?: number; unitTypeName?: string }[]
  >([]);
  const [addFeeLoading, setAddFeeLoading] = useState<boolean>(false);
  const [selectedFeeId, setSelectedFeeId] = useState<number | undefined>(
      undefined
  );
  const [appProductsModal, setAppProductsModal] =
      useState<ApplicableProductsModalState>({
        visible: false,
        row: null,
        items: [],
        selectedProductIds: [],
        editingTypeId: null,
        selectionTarget: undefined,
        minDuration: null,
        maxDuration: null,
        hadItems: false,
      });
    const [hoveredFeeKey, setHoveredFeeKey] = useState<string | null>(null);

     const { token } = theme.useToken();

      useImperativeHandle(
          ref,
          () => ({
            beginAdd: () => setAddModalVisible(true),
          }),
          []
      );

      const getFeeHoverKey = useCallback(
          (row: FeeRow) =>
              String(row.entry.productIdText ?? row.entry.feeId?.id ?? row.key),
          []
    );

    const feeRowEvents = useCallback(
        (row: FeeFlatRow) => {
          const k = getFeeHoverKey(row.feeRow);
          const feeId =
            row.feeRow.entry.feeId?.id ?? row.feeRow.entry.feeId ?? "";
          const highlightKey = `Fee-${feeId}`;
          const bgColor = props.rowHighlightColors?.[highlightKey];
          return {
            onMouseEnter: () => setHoveredFeeKey(k),
            onMouseLeave: () =>
              setHoveredFeeKey((cur) => (cur === k ? null : cur)),
            ...(bgColor
              ? ({ style: { "--pp-row-highlight": bgColor } as React.CSSProperties })
              : {}),
          };
        },
        [getFeeHoverKey, props.rowHighlightColors]
    );

    const canEditRow = useCallback(
      (row: FeeRow) => props.editMode && !row.entry.hasPendingChanges,
      [props.editMode]
    );

    const centreCurrencyIso = useMemo(() => {
      const centre = centres.find((c) => c.code === selectedCentreCode);
      return (centre?.currencyCode ?? "EUR").toUpperCase();
    }, [centres, selectedCentreCode]);

    const enabledCurrencies = useMemo(
          () =>
              (props.enabledCurrencies?.length
                      ? props.enabledCurrencies
                      : [centreCurrencyIso || "EUR"]
              ).map((x) => String(x).toUpperCase()),
          [props.enabledCurrencies, centreCurrencyIso]
      );

    const selectedPricingYearId: number | undefined =
      selectedPricingYear ??
      (pricingYears?.length
        ? Number(pricingYears[0].id)
        : undefined);

  const selectedPricingYearObj = useMemo(
      () =>
          pricingYears?.find(
              (x) => Number(x.id) === Number(selectedPricingYearId)
          ),
      [pricingYears, selectedPricingYearId]
  );

  const selectedYear = useMemo(
      () => selectedPricingYearObj?.year ?? dayjs().year(),
      [selectedPricingYearObj]
  );

  const {
    allowedStartDate: supplementAllowedStartDate,
    allowedEndDate: supplementAllowedEndDate,
    allowedStart: supplementAllowedStart,
    allowedEnd: supplementAllowedEnd,
  } = useMemo(() => {
    const window = getPricingYearWindow(selectedPricingYearObj);
    return {
      allowedStartDate: props.allowedStartDate ?? window.allowedStartDate,
      allowedEndDate: props.allowedEndDate ?? window.allowedEndDate,
      allowedStart: dayjs(props.allowedStartDate ?? window.allowedStartDate),
      allowedEnd: dayjs(props.allowedEndDate ?? window.allowedEndDate),
    };
  }, [
    selectedPricingYearObj,
    props.allowedStartDate,
    props.allowedEndDate,
  ]);

      const divisionId = useMemo(() => getDivisionId(), []);

      const handleImmediateSchoolStatusAction = useCallback(
          async (row: FeeRow, mode: "delete" | "recover") => {
            const pricingYearValue =
                pricingYears.find(
                    (py) => Number(py.id) === Number(selectedPricingYear)
                )?.year;

            if (!pricingYearValue) {
              message.error("Pricing year not found.");
              return;
            }

            const schoolIds = (row.entry.schoolStatuses ?? [])
                .filter((s: any) =>
                    mode === "delete" ? s?.status === "Active" : s?.status === "Inactive"
                )
                .map((s: any) =>
                    Number((s as any)?.schoolId?.id ?? (s as any)?.schoolId)
                )
                .filter((id: number) => Number.isFinite(id))
                .map((id) => ({ id }));

            if (schoolIds.length === 0) {
              message.info(
                  mode === "delete"
                      ? "No active schools to deactivate."
                      : "No inactive schools to activate."
              );
              return;
            }

            const productIdText = String(
                row.entry.productIdText ?? `Fee-${row.entry.feeId?.id ?? ""}`
            );

            const result =
                mode === "delete"
                    ? await api.DeactivateOfferings(
                        {
                          type: "Fee",
                          pricingYear: pricingYearValue,
                          productIdText,
                          schoolIds,
                          decommissionDate: DateHelper.ToDateString(
                              new Date(),
                              "yyyy-MM-dd"
                          ),
                        },
                        divisionId
                    )
                    : await api.ActivateOfferings(
                        {
                          type: "Fee",
                          pricingYear: pricingYearValue,
                          productIdText,
                          schoolIds,
                        },
                        divisionId
                    );

            if (result?.ok === false) {
              message.error(
                  result?.message ??
                  (mode === "delete"
                      ? "Failed to deactivate fee."
                      : "Failed to activate fee.")
              );
              return;
            }

            message.success(
                mode === "delete" ? "Fee deactivated successfully." : "Fee activated successfully."
            );

            props.reloadPricelist();
          },
          [
            divisionId,
            pricingYears,
            selectedPricingYear,
            props.reloadPricelist,
          ]
      );

      useEffect(() => {
        let cancelled = false;

      const load = async () => {
        try {
          const result = await api.getAllUnitTypes();
          if (!cancelled && Array.isArray(result)) setUnitTypes(result);
        } catch (e) {
          console.warn("Failed to load unit types", e);
        }
      };

      load();
      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      if (!addModalVisible) {
        setSelectedFeeId(undefined);
        return;
      }

      let cancelled = false;

      const load = async () => {
        try {
          setAddFeeLoading(true);
          const fees = await api.getAllFees();

          if (!cancelled && Array.isArray(fees)) {
            const draftList = draftPricelist ?? [];

            const existingFeeIds = new Set<number>(
              draftList
                .filter(isFeeOffering)
                .map((e) => {
                  const f = e as FeeOfferingDto;
                  const id = Number(f.feeId?.id);
                  return Number.isFinite(id) ? id : null;
                  })
                .filter((x): x is number => x != null)
            );

            const available = fees
              .filter((f: any) => f.active === true)
              .filter((f: any) => !existingFeeIds.has(Number(f.id)))
              .map((f: any) => ({
                id: Number(f.id),
                name: String(f.name),
                unitTypeId:
                  f.unitTypeId != null ? Number(f.unitTypeId) : undefined,
                unitTypeName:
                  f.unitTypeName != null ? String(f.unitTypeName) : undefined,
              }));

            setAvailableFees(available);
          }
        } catch (e) {
          console.warn("Failed to load fees", e);
          setAvailableFees([]);
        } finally {
          if (!cancelled) setAddFeeLoading(false);
        }
      };

      load();
      return () => {
        cancelled = true;
      };
    }, [addModalVisible, draftPricelist]);



    const productTypeOptions = useMemo(
      () =>
        (Object.keys(PRODUCT_TYPE_LABELS) as ProductTypeKey[]).map((value) => ({
          value,
          label: PRODUCT_TYPE_LABELS[value],
        })),
      []
    );

    type SimpleOption = { id: string; name: string };

    const optionKey = (type: ProductTypeKey, target: ApplicableTarget) =>
      `${type}|${target}`;

    const fetchProductsForType = useCallback(
      async (
        type: ProductTypeKey,
        target: ApplicableTarget
      ): Promise<SimpleOption[]> => {
        switch (type) {
          case "Course": {
            const list = await api.getCourseProducts(divisionId);
            const map = new Map<string, string>();
            (list ?? []).forEach((p: any) => {
              if (p?.active === false) return;
              map.set(String(p.id), String(p.name));
            });
            return [...map.entries()].map(([id, name]) => ({ id, name }));
          }

          case "Accommodation": {
            if (target === "Types") {
              const list = await api.getAllAccommodationTypes();
              return (list ?? [])
                .map((t: any) => ({ id: String(t.id), name: String(t.name) }))
                .sort((a, b) => a.name.localeCompare(b.name));
            } else {
              const list = (await api.getAccommodationRoomProductsByDivision(divisionId)) ?? [];
              const map = new Map<string, string>();
              (list as any[])
                .forEach((p: any) => {
                  if (p?.active === false) return;
                  map.set(String(p.id), String(p.name));
                });
              return [...map.entries()].map(([id, name]) => ({ id, name }));
            }
          }

          case "AddOn": {
            if (target === "Types") {
              const list = await api.getAllAddonTypes();
              return (list ?? [])
                .map((t: any) => ({ id: String(t.id), name: String(t.name) }))
                .sort((a, b) => a.name.localeCompare(b.name));
            } else {
              const list = await api.getAddonsProducts(divisionId);
              const map = new Map<string, string>();
              (list ?? []).forEach((p: any) => {
                if (p?.active === false) return;
                map.set(String(p.id), String(p.name ?? p.typeText ?? ""));
              });
              return [...map.entries()].map(([id, name]) => ({ id, name }));
            }
          }

          case "Transfer": {
            const list = await api.getTransferProducts(divisionId);
            const map = new Map<string, string>();
            (list ?? []).forEach((p: any) => {
              if (p?.active === false) return;
              map.set(String(p.id), String(p.name));
            });
            return [...map.entries()].map(([id, name]) => ({ id, name }));
          }

          case "Package": {
            const list = await api.getPackageProducts(divisionId);
            const map = new Map<string, string>();
            (list ?? []).forEach((p: any) => {
              if (p?.active === false) return;
              map.set(String(p.id), String(p.name));
            });
            return [...map.entries()].map(([id, name]) => ({ id, name }));
          }

            default:
              return [];
        }
      },
      [divisionId]
    );

    const [allProductsByKey, setAllProductsByKey] = useState<Map<string, SimpleOption[]>>(new Map());
    const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
    const loadedKeysRef = React.useRef<Set<string>>(new Set());

    useEffect(() => {
      if (!appProductsModal.visible) return;
      const productType = appProductsModal.selectedProductType;
      if (!productType) return;

      const target: ApplicableTarget = supportsTypesAndProducts(productType)
        ? appProductsModal.selectionTarget ?? DEFAULT_TARGET_FOR_GROUPED
        : "Products";

      const key = optionKey(productType, target);
      if (loadedKeysRef.current.has(key)) return;

      let cancelled = false;

      (async () => {
        try {
          setLoadingProducts(true);
          const list = await fetchProductsForType(productType, target);
          if (cancelled) return;

          setAllProductsByKey((prev) => {
            const next = new Map(prev);
            next.set(key, list);
            return next;
          });

          loadedKeysRef.current.add(key);
        } finally {
          if (!cancelled) setLoadingProducts(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [
      appProductsModal.visible,
      appProductsModal.selectedProductType,
      appProductsModal.selectionTarget,
      fetchProductsForType,
    ]);

    const productOptionsByKey = useMemo(() => {
      const options: Record<string, { label: string; value: string }[]> = {};
      allProductsByKey.forEach((list, key) => {
        options[key] = (list ?? [])
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((x) => ({ label: x.name, value: x.id }));
      });
      return options;
    }, [allProductsByKey]);

    const feeRows: FeeRow[] = useMemo(() => {
      const list = draftPricelist ?? [];
      if (!Array.isArray(list)) return [];

      const unitTypeNameById = new Map<number, string>();
      unitTypes.forEach((u) => unitTypeNameById.set(Number(u.id), u.name));

      const rows = list
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => isFeeOffering(entry))
        .map(({ entry, index }) => {
          const fee = entry as FeeOfferingDto;
          const unitTypeName = unitTypeNameById.get(Number(fee.feeUnitTypeId));
          const backendOfferingType =
            fee.feeOfferingType != null
              ? normalizeFeeOfferingType(fee.feeOfferingType)
              : undefined;
          const offeringType =
            backendOfferingType ??
            deriveFeeOfferingTypeFromUnitTypeId(Number(fee.feeUnitTypeId));
          const base = fee.productIdText || `Fee-${fee.feeId?.id ?? ""}`;

          return {
            key: `${base}|${index}`,
            index,
            entry: fee,
            unitTypeName,
            offeringType,
          };
        })

        const filteredRows = props.showInactiveOnly
            ? rows
            : rows.filter((r) => {
              const statuses = r.entry.schoolStatuses ?? [];
              return r.entry.isNew || statuses.some((s: any) => s?.status === "Active");
            });

        return filteredRows.sort(
            (a, b) =>
                Number(!!b.entry.isNew) - Number(!!a.entry.isNew) ||
                Number(!!b.entry.isUpdated) - Number(!!a.entry.isUpdated) ||
                String(a.entry.name ?? "").localeCompare(String(b.entry.name ?? ""))
        );
      }, [draftPricelist, unitTypes, props.showInactiveOnly]);

      const feeGroupOrderMap = useMemo(() => {
        const map = new Map<string, number>();
        let order = 0;

        feeRows.forEach((row) => {
          const key = getFeeHoverKey(row);
          if (!map.has(key)) {
            map.set(key, order++);
          }
        });

        return map;
      }, [feeRows, getFeeHoverKey]);

      const feeRowClassName = useCallback(
          (row: FeeFlatRow) => {
            const groupKey = getFeeHoverKey(row.feeRow);
            const groupOrder = feeGroupOrderMap.get(groupKey) ?? 0;
            const base = groupOrder % 2 === 0 ? "even-row" : "odd-row";
            const hovered = hoveredFeeKey === groupKey ? "hovered-row" : undefined;
            const feeId = row.feeRow.entry.feeId?.id ?? row.feeRow.entry.feeId ?? "";
            const highlightKey = `Fee-${feeId}`;
            const hasHighlight = Boolean(props.rowHighlightColors?.[highlightKey]);
            return [base, hovered, hasHighlight ? "pp-row-highlighted" : undefined]
              .filter(Boolean)
              .join(" ");
          },
          [feeGroupOrderMap, hoveredFeeKey, getFeeHoverKey, props.rowHighlightColors]
      );

      const flatRows: FeeFlatRow[] = useMemo(() => {
        return feeRows.flatMap<FeeFlatRow>((feeRow) => {
          if (feeRow.offeringType !== "Supplement") {
            return [
              {
                key: `${feeRow.key}|single`,
                feeRow,
                periodIndex: null,
                period: null,
                feeRowSpan: 1,
                isFirstPeriodRow: true,
              },
            ];
          }

          if (!feeRow.entry.supplementHasSpecificDates) {
            const allDatesPeriod = (feeRow.entry.supplementPeriods ?? [])[0] ?? null;

            return [
              {
                key: `${feeRow.key}|all-dates`,
                feeRow,
                periodIndex: allDatesPeriod ? 0 : null,
                period: allDatesPeriod,
                feeRowSpan: 1,
                isFirstPeriodRow: true,
              },
            ];
          }

          const periods = feeRow.entry.supplementPeriods ?? [];
          if (periods.length === 0) {
            return [
              {
                key: `${feeRow.key}|empty-period`,
                feeRow,
                periodIndex: null,
                period: null,
                feeRowSpan: 1,
                isFirstPeriodRow: true,
              },
            ];
          }

          return periods.map((period, index) => ({
            key: `${feeRow.key}|period-${index}`,
            feeRow,
            periodIndex: index,
            period,
            feeRowSpan: periods.length,
            isFirstPeriodRow: index === 0,
          }));
        });
      }, [feeRows]);

    const clone = (x: FeeOfferingDto): FeeOfferingDto =>
      JSON.parse(JSON.stringify(x));

    const updateFeeEntry = useCallback(
      (row: FeeRow, updater: (draft: FeeOfferingDto) => void) => {
        if (!draftPricelist) return;
        if (!props.editMode) return;
        if (row.entry.hasPendingChanges) return;

        const source = draftPricelist[row.index];
        if (!source || !isFeeOffering(source)) return;

        const draft = clone(source as FeeOfferingDto);
        updater(draft);
        sanitizeFeeByUnitType(draft, unitTypes, row.unitTypeName);
        draft.isUpdated = true;

        dispatch(updateDraftPricelistItem(draft, row.index));
        dispatch(setFormState(SaveState.PendingChanges));
      },
      [
        draftPricelist,
        props.editMode,
        dispatch,
        unitTypes,
      ]
    );

    const normalizeAge = (v: number | null): number | null => {
        if (typeof v !== "number" || !Number.isFinite(v)) return null;
        const n = Math.trunc(v);
        return n > 0 ? n : null;
    };

    const handleAgeChange = useCallback(
        (
            row: FeeRow,
            field: "minimumAge" | "maximumAge",
            value: number | null
        ) => {
          updateFeeEntry(row, (draft) => {
            (draft as any)[field] = normalizeAge(value);
          });
        },
        [updateFeeEntry]
    );

  const handleSpecificDatesToggle = useCallback(
      (row: FeeRow, checked: boolean) => {
        updateFeeEntry(row, (draft) => {
          draft.supplementHasSpecificDates = checked;
          draft.supplementPeriods = Array.isArray(draft.supplementPeriods)
              ? [...draft.supplementPeriods]
              : [];

          if (checked) {
            const hasSpecificRows = draft.supplementPeriods.some(
                (p: any) => toIso(p?.startDate) || toIso(p?.endDate)
            );

            if (!hasSpecificRows) {
              draft.supplementPeriods = [
                {
                  year: Number(selectedYear),
                  startDate: "",
                  endDate: "",
                  prices:
                      draft.supplementPeriods[0]?.prices?.map((p: any) => ({
                        currencyIsoCode: normalizeCurrencyIso(p?.currencyIsoCode),
                        pricePerMajorUnit: Number(p?.pricePerMajorUnit ?? 0),
                        pricePerMinorUnit: Number(p?.pricePerMinorUnit ?? 0),
                      })) ??
                      enabledCurrencies.map((currencyIsoCode) => ({
                        currencyIsoCode: normalizeCurrencyIso(currencyIsoCode),
                        pricePerMajorUnit: 0,
                        pricePerMinorUnit: 0,
                      })),
                },
              ];
            }

            return;
          }

          if (!checked) {
            draft.supplementPeriods = [
              buildAllDatesPeriodFromSpecific(
                  draft.supplementPeriods as SupplementPeriodItem[],
                  selectedYear,
                  enabledCurrencies
              ),
            ];
          }
        });
      },
      [updateFeeEntry, enabledCurrencies, selectedYear]
  );

    const handleHasMaximumToggle = useCallback(
      (row: FeeRow, checked: boolean) => {
        updateFeeEntry(row, (draft) => {
          const unitTypeName = getUnitTypeNameForFee(
            draft.feeUnitTypeId,
            unitTypes,
            row.unitTypeName
          );
          const rules = getUnitRules(draft.feeUnitTypeId, unitTypeName);

          if (!draft.supplementMaximumUnits) {
            draft.supplementMaximumUnits = {
              hasMaximum: checked,
              majorUnits: 0,
              minorUnits: 0,
            };
            return;
          }

          draft.supplementMaximumUnits = {
            ...draft.supplementMaximumUnits,
            hasMaximum: checked,
            majorUnits: checked
              ? Number(draft.supplementMaximumUnits.majorUnits ?? 0)
              : 0,
            minorUnits: checked
              ? Number(draft.supplementMaximumUnits.minorUnits ?? 0)
              : 0,
          };

          if (!rules.allowsMinor) {
            draft.supplementMaximumUnits.minorUnits = 0;
          }
        });
      },
      [updateFeeEntry, unitTypes]
    );

  const handleSupplementPeriodDateChange = useCallback(
      (
          row: FeeRow,
          periodIndex: number,
          field: "startDate" | "endDate",
          value: Dayjs | null
      ) => {
        updateFeeEntry(row, (draft) => {
          draft.supplementHasSpecificDates = true;

          const periods = [...(draft.supplementPeriods ?? [])];
          const current = periods[periodIndex];
          if (!current) return;

          const currentStart = String(current.startDate ?? "");
          const currentEnd = String(current.endDate ?? "");

          const minForField =
              field === "startDate"
                  ? supplementAllowedStartDate
                  : maxIsoDate(currentStart, supplementAllowedStartDate);

          const maxForField =
              field === "startDate"
                  ? minIsoDate(currentEnd || supplementAllowedEndDate, supplementAllowedEndDate)
                  : supplementAllowedEndDate;

          const normalizedIso = shiftPickerDateToEarliestAllowed(
              value,
              minForField,
              maxForField
          );

          periods[periodIndex] = {
            ...current,
            year: Number(current.year ?? selectedYear),
            [field]: normalizedIso,
          };

          draft.supplementPeriods = periods;
        });
      },
      [
        updateFeeEntry,
        selectedYear,
        supplementAllowedStartDate,
        supplementAllowedEndDate,
      ]
  );

  const handleSupplementPeriodPriceChange = useCallback(
      (
          row: FeeRow,
          periodIndex: number,
          currencyIsoCode: string,
          field: "pricePerMajorUnit" | "pricePerMinorUnit",
          value: number | null
      ) => {
        updateFeeEntry(row, (draft) => {
          const periods = [...(draft.supplementPeriods ?? [])];
          let period = periods[periodIndex];
          if (!period) {
            period = createEmptySupplementPeriod(selectedYear, enabledCurrencies);
            periods[periodIndex] = period;
          }

          const prices = [...(period.prices ?? [])];
          const idx = prices.findIndex(
              (x: any) =>
                  String(x.currencyIsoCode).toUpperCase() ===
                  String(currencyIsoCode).toUpperCase()
          );

          const n = Number(value ?? 0);

          if (idx >= 0) {
            prices[idx] = { ...prices[idx], [field]: n };
          } else {
            prices.push({
              currencyIsoCode,
              pricePerMajorUnit: field === "pricePerMajorUnit" ? n : 0,
              pricePerMinorUnit: field === "pricePerMinorUnit" ? n : 0,
            });
          }

          periods[periodIndex] = {
            ...period,
            year: Number(period.year ?? selectedYear),
            prices,
          };

          draft.supplementPeriods = periods;
        });
      },
      [updateFeeEntry, selectedYear, enabledCurrencies]
  );

  const removeSupplementPeriod = useCallback(
      (row: FeeRow, periodIndex: number) => {
        updateFeeEntry(row, (draft) => {
          const periods = [...(draft.supplementPeriods ?? [])];
          periods.splice(periodIndex, 1);
          draft.supplementPeriods = periods;
        });
      },
      [updateFeeEntry]
  );

  const insertSupplementPeriodRelative = useCallback(
      (row: FeeRow, periodIndex: number, direction: "above" | "below") => {
        updateFeeEntry(row, (draft) => {
          const periods = [...(draft.supplementPeriods ?? [])];
          const reference = periods[periodIndex];

          let startDate = "";
          let endDate = "";

          if (direction === "below" && reference?.endDate) {
            const next = dayjs(reference.endDate)
                .add(1, "day")
                .format("YYYY-MM-DD");
            startDate = next;
            endDate = next;
          }

          if (direction === "above" && reference?.startDate) {
            const prev = dayjs(reference.startDate)
                .subtract(1, "day")
                .format("YYYY-MM-DD");
            startDate = prev;
            endDate = prev;
          }

          const newPeriod: SupplementPeriodItem = {
            year: Number(reference?.year ?? selectedYear),
            startDate,
            endDate,
            prices: enabledCurrencies.map(createSupplementCurrencyPrice),
          };

          const insertIndex = direction === "above" ? periodIndex : periodIndex + 1;
          periods.splice(insertIndex, 0, newPeriod);

          draft.supplementHasSpecificDates = true;
          draft.supplementPeriods = periods;
        });
      },
      [enabledCurrencies, updateFeeEntry, selectedYear]
  );

    const handleMaximumUnitsChange = useCallback(
      (
        row: FeeRow,
        field: "majorUnits" | "minorUnits",
        value: number | null
      ) => {
        updateFeeEntry(row, (draft) => {
          const unitTypeName = getUnitTypeNameForFee(
            draft.feeUnitTypeId,
            unitTypes,
            row.unitTypeName
          );
          const rules = getUnitRules(draft.feeUnitTypeId, unitTypeName);

          if (!draft.supplementMaximumUnits) {
            draft.supplementMaximumUnits = {
              hasMaximum: true,
              majorUnits: 0,
              minorUnits: 0,
            };
          }

          if (field === "minorUnits" && !rules.allowsMinor) {
            draft.supplementMaximumUnits = {
              ...draft.supplementMaximumUnits,
              hasMaximum: true,
              minorUnits: 0,
            };
            return;
          }

          const nextVal = value == null ? 0 : Number(value);
          const boundedMinor =
            field === "minorUnits" && rules.minorMax != null
              ? Math.min(nextVal, rules.minorMax)
              : nextVal;

          draft.supplementMaximumUnits = {
            ...draft.supplementMaximumUnits,
            hasMaximum: true,
            [field]: boundedMinor,
          };
        });
      },
      [updateFeeEntry, unitTypes]
    );

  const openApplicableProductsModal = useCallback((row: FeeRow) => {
    const cloned = (row.entry.applicableProducts ?? []).map((x) => ({
      ...x,
      productIds: [...(x.productIds ?? [])],
      accommodationTypeIds: [...(x.accommodationTypeIds ?? [])],
      addOnTypeIds: [...(x.addOnTypeIds ?? [])],
    }));

    const durationState = getApplicableDurationState(row, cloned);

    setAppProductsModal({
      visible: true,
      row,
      items: cloned,
      ...getEmptyApplicableEditorState(),
      minDuration: durationState.minDuration,
      maxDuration: durationState.maxDuration,
      hadItems: cloned.length > 0,
    });
  }, []);

    const closeApplicableProductsModal = useCallback(() => {
      setAppProductsModal({
        visible: false,
        row: null,
        items: [],
        selectedProductType: undefined,
        selectionMode: undefined,
        selectionTarget: undefined,
        selectedProductIds: [],
        editingTypeId: null,
        minDuration: null,
        maxDuration: null,
        error: undefined,
        hadItems: false,
      });
    }, []);

    const handleApplicableEdit = useCallback((productTypeId: number) => {
      setAppProductsModal((prev) => {
        const item = prev.items.find(
          (x) => Number(productTypeIdFromApplicableItem(x)) === Number(productTypeId)
        );
        if (!item) return prev;

        const ptName = PRODUCT_TYPE_ID_TO_NAME[Number(productTypeId)];
        const mode = normalizeSelectionMode(item.selectionMode);
        const inferredTarget = inferTargetFromItem(item);
        const target: ApplicableTarget = supportsTypesAndProducts(ptName)
          ? inferredTarget
          : "Products";
        const ids = getApplicableIds(item, target).map(String);

        return {
          ...prev,
          selectedProductType: ptName,
          selectionMode: mode,
          selectionTarget: supportsTypesAndProducts(ptName) ? target : "Products",
          selectedProductIds: ids,
          editingTypeId: Number(productTypeId),
          error: undefined,
        };
      });
    }, []);

    const handleApplicableDelete = useCallback((productTypeId: number) => {
      setAppProductsModal((prev) => ({
        ...prev,
        items: prev.items.filter(
          (x) => Number(productTypeIdFromApplicableItem(x)) !== Number(productTypeId)
        ),
        editingTypeId:
          prev.editingTypeId === productTypeId ? null : prev.editingTypeId,
      }));
    }, []);

    const validateApplicableItemsForSave = (items: ApplicableProduct[]): string | undefined => {
      if (!items || items.length === 0) {
        return PRICELIST_VALIDATION_MESSAGES.applicableProductsRequired;
      }

      for (const item of items) {
        const productTypeId = Number(item.productTypeId ?? 0);
        const productTypeName = PRODUCT_TYPE_ID_TO_NAME[productTypeId];
        const label = productTypeName ? PRODUCT_TYPE_LABELS[productTypeName] : `ProductType ${productTypeId}`;

        const mode = normalizeSelectionMode(item.selectionMode);
        const target: ApplicableTarget = productTypeName && supportsTypesAndProducts(productTypeName)
            ? (inferTargetFromItem(item) ?? DEFAULT_TARGET_FOR_GROUPED)
            : "Products";

        const ids = getApplicableIds(item, target);

        if (mode !== "SpecificProductType") {
          if (target === "Types" && ids.length !== 1) {
            return `${label}: select exactly one type.`;
          }
          if (target === "Products" && ids.length === 0) {
            return `${label}: select at least one product.`;
          }
        }
      }

        const isOneOff = appProductsModal.row?.offeringType === "One Off";
        if (
            isOneOff &&
            appProductsModal.minDuration != null &&
            appProductsModal.maxDuration != null &&
            Number(appProductsModal.maxDuration) < Number(appProductsModal.minDuration)
        ) {
          return "Max Duration must be greater than or equal to Min Duration.";
        }

      return undefined;
    };

    const applicableSaveError = appProductsModal.visible
        ? validateApplicableItemsForSave(appProductsModal.items)
        : undefined;

    const showApplicableEmptyError =
        appProductsModal.visible &&
        appProductsModal.hadItems &&
        appProductsModal.items.length === 0;

    const handleApplicableAddOrUpdate = useCallback(() => {
      setAppProductsModal((prev) => {
        const pt = prev.selectedProductType;
        const mode = prev.selectionMode;

        if (!pt) return { ...prev, error: "Product type is required." };
        if (!mode) return { ...prev, error: "Selection type is required." };

        const productTypeId = PRODUCT_TYPE_NAME_TO_ID[pt];
        const target: ApplicableTarget = supportsTypesAndProducts(pt)
            ? prev.selectionTarget ?? DEFAULT_TARGET_FOR_GROUPED
            : "Products";

        if (mode !== "SpecificProductType") {
          if (target === "Types" && prev.selectedProductIds.length !== 1) {
            return { ...prev, error: "Select exactly one type." };
          }
          if (target === "Products" && prev.selectedProductIds.length === 0) {
            return { ...prev, error: "Select at least one product." };
          }
        }

        const ids = prev.selectedProductIds
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n));

        const base: ApplicableProduct = {
          productTypeId,
          selectionMode: mode,
          productIds: [],
          accommodationTypeIds: [],
          addOnTypeIds: [],
        };

        const nextItem =
            mode === "SpecificProductType"
                ? setApplicableIds(base, [], target)
                : setApplicableIds(base, ids, target);

        const existingIdx = prev.items.findIndex(
            (x) => Number(x.productTypeId) === Number(productTypeId)
        );
        const next = [...prev.items];
        if (existingIdx !== -1) next[existingIdx] = { ...next[existingIdx], ...nextItem };
        else next.push(nextItem);

        return {
          ...prev,
          items: next,
          selectedProductType: undefined,
          selectionMode: undefined,
          selectionTarget: undefined,
          selectedProductIds: [],
          editingTypeId: null,
          error: undefined,
          hadItems: true,
        };
      });
    }, []);

    const handleApplicableSave = useCallback(() => {
      setAppProductsModal((prev) => {
        if (!prev.row) return prev;

        const itemsToSave = [...(prev.items ?? [])];
        const err = validateApplicableItemsForSave(itemsToSave);

        if (err) {
          return { ...prev, error: err };
        }

        updateFeeEntry(prev.row, (draft) => {
          draft.applicableProducts = itemsToSave.map((x) => ({
            ...x,
            productIds: x.productIds ?? [],
            accommodationTypeIds: x.accommodationTypeIds ?? [],
            addOnTypeIds: x.addOnTypeIds ?? [],
            ...(prev.row?.offeringType === "One Off"
                ? {
                  minDuration: prev.minDuration,
                  maxDuration: prev.maxDuration,
                }
                : {
                  minDuration: null,
                  maxDuration: null,
                }),
          }));
        });

        return {
          visible: false,
          row: null,
          items: [],
          selectedProductType: undefined,
          selectionMode: undefined,
          selectionTarget: undefined,
          selectedProductIds: [],
          editingTypeId: null,
          minDuration: null,
          maxDuration: null,
          error: undefined,
          hadItems: false,
        };
      });
    }, [updateFeeEntry]);

    const handleAddFee = useCallback(() => {
      if (!selectedFeeId) return;

      const alreadyExists = (draftPricelist ?? [])
          .filter(isFeeOffering)
          .some((e) => {
            const f = e as FeeOfferingDto;
            return Number(f.feeId?.id) === Number(selectedFeeId);
          });

      if (alreadyExists) {
        message.warning("This fee already exists.");
        return;
      }

      const selectedFee = availableFees.find((f) => f.id === selectedFeeId);
      if (!selectedFee) return;

      const unitTypeId = Number(selectedFee.unitTypeId ?? 0);
      const unitTypeName =
          selectedFee.unitTypeName ??
          unitTypes.find((u) => Number(u.id) === Number(unitTypeId))?.name ??
          undefined;

      const feeOfferingType = deriveFeeOfferingTypeFromUnitTypeId(unitTypeId);

      const newEntry: FeeOfferingDto = {
        offeringType: "fee",
        type: "Fee" as unknown as EditOfferingType,
        productIdText: `Fee-${selectedFeeId}`,
        name: selectedFee.name,
        hasPendingChanges: false,
        isUpdated: false,
        isNew: true,
        schoolIds: [],
        schoolStatuses: [],
        feeId: { id: selectedFeeId },
        feeUnitTypeId: unitTypeId,
        feeOfferingType,
        minimumAge: null,
        maximumAge: null,
        supplementHasSpecificDates: false,
        supplementPeriods: [],
        supplementMaximumUnits: {
          hasMaximum: false,
          majorUnits: 0,
          minorUnits: 0,
        },
        oneOffFeePrices: enabledCurrencies.map((currencyIsoCode) => ({
          year: selectedYear,
          currencyIsoCode,
          amount: 0,
        })),
        applicableProducts: [],
      };

      if (feeOfferingType === "Supplement") {
        newEntry.supplementHasSpecificDates = true;
        newEntry.supplementPeriods = [
          createEmptySupplementPeriod(selectedYear, enabledCurrencies),
        ];
        newEntry.oneOffFeePrices = [];
      }else {
        enabledCurrencies.forEach((currencyIsoCode) =>
            getOrCreateOneOffPriceIndex(newEntry, selectedYear, currencyIsoCode)
        );
      }

      sanitizeFeeByUnitType(newEntry, unitTypes, unitTypeName);

      dispatch(loadDraftPricelist([newEntry, ...(draftPricelist ?? [])]));
      dispatch(setFormState(SaveState.PendingChanges));
      setAddModalVisible(false);
      setSelectedFeeId(undefined);
    }, [
      selectedFeeId,
      availableFees,
      unitTypes,
      selectedYear,
      enabledCurrencies,
      draftPricelist,
      dispatch,
    ]);

    const getFieldError = useCallback(
      (fieldName: string): string | undefined => {
        if (!props.editMode) return undefined;
        const err = formErrors.find((e) => e.name === fieldName);
        return err?.error;
      },
      [props.editMode, formErrors]
    );

    const validateFees = useCallback(() => {
      if (!props.editMode) return;

      const errors: FormError[] = [];
      feeRows
        .filter((row) => row.entry.isNew === true && !row.entry.hasPendingChanges)
        .forEach((row) => {
          const entry = row.entry;

          if (entry.minimumAge == null) {
            errors.push({
              name: `fee-${row.key}-minAge`,
              error: "Required",
              message: "Required",
              field: undefined,
            });
          }

          if (entry.maximumAge == null) {
            errors.push({
              name: `fee-${row.key}-maxAge`,
              error: "Required",
              message: "Required",
              field: undefined,
            });
          }

          if (entry.isNew && !(entry.schoolIds?.length > 0)) {
            errors.push({
              name: `fee-${row.key}-schools`,
              error: "Select at least one school",
              message: "Select at least one school",
              field: undefined,
            });
          }

          if (
            row.offeringType === "One Off" &&
            (!(entry.applicableProducts?.length > 0))
          ) {
            errors.push({
              name: `fee-${row.key}-applicableProducts`,
              error: PRICELIST_VALIDATION_MESSAGES.applicableProductsRequired,
              message: PRICELIST_VALIDATION_MESSAGES.applicableProductsRequired,
              field: undefined,
            });
          }

          if (!entry.feeUnitTypeId || Number(entry.feeUnitTypeId) <= 0) {
            errors.push({
              name: `fee-${row.key}-unitType`,
              error: "Fee unit type is required",
              message: "Fee unit type is required",
              field: undefined,
            });
          }

          const minAge = entry.minimumAge != null ? Number(entry.minimumAge) : 0;
          const maxAge = entry.maximumAge != null ? Number(entry.maximumAge) : null;

          if (
              maxAge != null &&
              Number.isFinite(maxAge) &&
              maxAge > 0 &&
              maxAge <= (Number.isFinite(minAge) ? minAge : 0)
          ) {
            errors.push({
              name: `fee-${row.key}-maxAge`,
              error: "Maximum age must be greater than minimum age",
              message: "Maximum age must be greater than minimum age",
              field: undefined,
            });
          }

          if (row.offeringType === "Supplement") {
            if (entry.supplementHasSpecificDates) {
              if (!(entry.supplementPeriods?.length > 0)) {
                errors.push({
                  name: `fee-${row.key}-period`,
                  error: "At least one date range is required",
                  message: "At least one date range is required",
                  field: undefined,
                });
              }

              const ranges = [...(entry.supplementPeriods ?? [])].sort(
                  (a, b) =>
                      String(a.startDate).localeCompare(String(b.startDate)) ||
                      String(a.endDate).localeCompare(String(b.endDate))
              );

              ranges.forEach((range, idx) => {
                if (!range.startDate) {
                  errors.push({
                    name: `fee-${row.key}-range-${idx}-start`,
                    error: "Required",
                    message: "Required",
                    field: undefined,
                  });
                }

                if (!range.endDate) {
                  errors.push({
                    name: `fee-${row.key}-range-${idx}-end`,
                    error: "Required",
                    message: "Required",
                    field: undefined,
                  });
                }

                if (
                    range.startDate &&
                    (String(range.startDate) < supplementAllowedStartDate ||
                        String(range.startDate) > supplementAllowedEndDate)
                ) {
                  errors.push({
                    name: `fee-${row.key}-range-${idx}-start-outside-window`,
                    error: `Start date must be within ${supplementAllowedStartDate} and ${supplementAllowedEndDate}`,
                    message: `Start date must be within ${supplementAllowedStartDate} and ${supplementAllowedEndDate}`,
                    field: undefined,
                  });
                }

                if (
                    range.endDate &&
                    (String(range.endDate) < supplementAllowedStartDate ||
                        String(range.endDate) > supplementAllowedEndDate)
                ) {
                  errors.push({
                    name: `fee-${row.key}-range-${idx}-end-outside-window`,
                    error: `End date must be within ${supplementAllowedStartDate} and ${supplementAllowedEndDate}`,
                    message: `End date must be within ${supplementAllowedStartDate} and ${supplementAllowedEndDate}`,
                    field: undefined,
                  });
                }

                if (
                    range.startDate &&
                    range.endDate &&
                    dayjs(String(range.endDate)).isBefore(dayjs(String(range.startDate)), "day")
                ) {
                  errors.push({
                    name: `fee-${row.key}-range-${idx}-end-before-start`,
                    error: "End date must be on or after start date",
                    message: "End date must be on or after start date",
                    field: undefined,
                  });
                }

                enabledCurrencies.forEach((currencyIsoCode) => {
                  const price = (range.prices ?? []).find(
                      (x: any) =>
                          String(x.currencyIsoCode).toUpperCase() ===
                          String(currencyIsoCode).toUpperCase()
                  );

                  const majorPrice = Number(price?.pricePerMajorUnit);
                  if (
                    !price ||
                    !Number.isFinite(majorPrice) ||
                    majorPrice <= 0
                  ) {
                    errors.push({
                      name: `fee-${row.key}-range-${idx}-price-${currencyIsoCode}`,
                      error: `Price for ${currencyIsoCode} is required`,
                      message: `Price for ${currencyIsoCode} is required`,
                      field: undefined,
                    });
                  }
                });

                if (idx > 0) {
                  const prev = ranges[idx - 1];
                  if (
                      prev.endDate &&
                      range.startDate &&
                      dayjs(String(range.startDate)).isBefore(dayjs(String(prev.endDate)))
                  ) {
                    errors.push({
                      name: `fee-${row.key}-overlap-${idx}`,
                      error: "Supplement date ranges cannot overlap",
                      message: "Supplement date ranges cannot overlap",
                      field: undefined,
                    });
                  }
                }
              });
            } else {
              if (!(entry.supplementPeriods?.length > 0)) {
                errors.push({
                  name: `fee-${row.key}-all-dates-period`,
                  error: "At least one all-dates price row is required",
                  message: "At least one all-dates price row is required",
                  field: undefined,
                });
              } else {
                const allDatesRow = entry.supplementPeriods[0];

                enabledCurrencies.forEach((currencyIsoCode) => {
                  const price = (allDatesRow?.prices ?? []).find(
                      (x: any) =>
                          String(x.currencyIsoCode).toUpperCase() ===
                          String(currencyIsoCode).toUpperCase()
                  );

                  const majorPrice = Number(price?.pricePerMajorUnit);
                  if (
                    !price ||
                    !Number.isFinite(majorPrice) ||
                    majorPrice <= 0
                  ) {
                    errors.push({
                      name: `fee-${row.key}-all-dates-price-${currencyIsoCode}`,
                      error: `Price for ${currencyIsoCode} is required`,
                      message: `Price for ${currencyIsoCode} is required`,
                      field: undefined,
                    });
                  }
                });
              }
            }

            if (entry.supplementMaximumUnits?.hasMaximum) {
              if ((entry.supplementMaximumUnits.majorUnits ?? 0) < 0) {
                errors.push({
                  name: `fee-${row.key}-maxMajor`,
                  error: "Maximum major units required",
                  message: "Maximum major units required",
                  field: undefined,
                });
              }
            }
          } else {
            enabledCurrencies.forEach((currencyIsoCode) => {
              const oneOffPrice = (entry.oneOffFeePrices ?? []).find(
                  (x: any) =>
                      x != null &&
                      Number(x.year) === Number(selectedYear) &&
                      String(x.currencyIsoCode).toUpperCase() ===
                      String(currencyIsoCode).toUpperCase()
              );
              const amount = Number(oneOffPrice?.amount);
              if (
                !oneOffPrice ||
                !Number.isFinite(amount) ||
                amount <= 0
              ) {
                errors.push({
                  name: `fee-${row.key}-price-${currencyIsoCode}`,
                  error: `One-off price for ${currencyIsoCode} is required`,
                  message: `One-off price for ${currencyIsoCode} is required`,
                  field: undefined,
                });
              }
            });
          }

          (entry.applicableProducts ?? []).forEach((x, idx) => {
            if (
                x.minDuration != null &&
                x.maxDuration != null &&
                Number(x.maxDuration) < Number(x.minDuration)
            ) {
              errors.push({
                name: `fee-${row.key}-duration-${idx}`,
                error: "Max Duration must be greater than or equal to Min Duration",
                message: "Max Duration must be greater than or equal to Min Duration",
                field: undefined,
              });
            }
          });
        });

      dispatch(setFormErrors(errors));
    }, [props.editMode, feeRows, dispatch, selectedYear, enabledCurrencies]);

    useEffect(() => {
      validateFees();
    }, [validateFees]);

    useEffect(() => {
      if (props.editMode) return;

      const current = formErrors ?? [];
      const next = current.filter((e) => !String(e.name ?? "").startsWith("fee-"));
      if (next.length !== current.length) {
        dispatch(setFormErrors(next));
      }
    }, [props.editMode, formErrors, dispatch]);

  const renderRowSpanCell = useCallback(
      (row: FeeFlatRow, children: React.ReactNode, extraProps?: Record<string, any>) => {
        const extraStyle = extraProps?.style ?? {};

        return row.isFirstPeriodRow
            ? {
              children,
              props: {
                rowSpan: row.feeRowSpan,
                ...(extraProps ?? {}),
                style: {
                  ...TOP_ALIGNED_CELL_STYLE,
                  ...extraStyle,
                },
              },
            }
            : { children: null, props: { rowSpan: 0 } };
      },
      []
  );

  const needsSupplementEditOffset = useCallback(
      (row: FeeFlatRow, editable: boolean) =>
          editable &&
          row.feeRow.offeringType === "Supplement" &&
          Boolean(row.feeRow.entry.supplementHasSpecificDates) &&
          row.isFirstPeriodRow,
      []
  );

  const renderWithSupplementEditOffset = useCallback(
      (row: FeeFlatRow, editable: boolean, child: React.ReactNode) => (
          <div style={FEE_EDIT_OFFSET_STACK_STYLE}>
            {needsSupplementEditOffset(row, editable) ? <CheckboxSpacer /> : null}
            {child}
          </div>
      ),
      [needsSupplementEditOffset]
  );

  const priceColumns = enabledCurrencies.map((currencyIsoCode) => ({
    title: (
      <div>
        {`Price (${currencyIsoCode})`}{" "}
        <Tooltip
          title={
            <div style={{ whiteSpace: "pre-line" }}>
              {PRICE_MAJOR_MINOR_UNIT_TOOLTIP}
            </div>
          }
        >
          <InfoCircleOutlined />
        </Tooltip>
      </div>
    ),
    children: [
      {
        title: "Major",
        width: PP_COL_W.price,
        className: "pp-price-cell",
        render: (_: unknown, row: FeeFlatRow) => {
          const editable = canEditRow(row.feeRow);

          if (row.feeRow.offeringType === "One Off") {
            const price = (row.feeRow.entry.oneOffFeePrices ?? []).find(
                (x: any) =>
                    x != null &&
                    Number(x.year) === Number(selectedYear) &&
                    String(x.currencyIsoCode).toUpperCase() === currencyIsoCode
            );
            const priceError = getFieldError(
              `fee-${row.feeRow.key}-price-${currencyIsoCode}`
            );

            return renderRowSpanCell(
                row,
                editable ? (
                    <InputNumber
                        size="small"
                        min={0}
                        controls={false}
                        precision={2}
                        step={0.01}
                        status={priceError ? "error" : undefined}
                        value={Number(price?.amount ?? 0)}
                        onFocus={selectAllOnPriceFocus}
                        onChange={(v) =>
                            updateFeeEntry(row.feeRow, (draft) => {
                              const idx = getOrCreateOneOffPriceIndex(
                                  draft,
                                  selectedYear,
                                  currencyIsoCode
                              );
                              draft.oneOffFeePrices[idx] = {
                                ...draft.oneOffFeePrices[idx],
                                amount: Number(v ?? 0),
                                currencyIsoCode,
                              };
                            })
                        }
                        style={FEE_PRICE_INPUT_STYLE}
                    />
                ) : (
                    <Text>{fmtMoney(price?.amount)}</Text>
                )
            );
          }

          const price = (row.period?.prices ?? []).find(
              (x: any) => String(x.currencyIsoCode).toUpperCase() === currencyIsoCode
          );
          const supplementPriceError =
            getFieldError(
              `fee-${row.feeRow.key}-range-${row.periodIndex ?? 0}-price-${currencyIsoCode}`
            ) ||
            getFieldError(
              `fee-${row.feeRow.key}-all-dates-price-${currencyIsoCode}`
            );

          return editable ? (
              renderWithSupplementEditOffset(
                  row,
                  editable,
                  <InputNumber
                      size="small"
                      min={0}
                      controls={false}
                      precision={2}
                      step={0.01}
                      status={supplementPriceError ? "error" : undefined}
                      value={Number(price?.pricePerMajorUnit ?? 0)}
                      onFocus={selectAllOnPriceFocus}
                      onChange={(v) =>
                          handleSupplementPeriodPriceChange(
                              row.feeRow,
                              Number(row.periodIndex ?? 0),
                              currencyIsoCode,
                              "pricePerMajorUnit",
                              typeof v === "number" ? v : null
                          )
                      }
                      style={FEE_PRICE_INPUT_STYLE}
                  />
              )
          ) : (
              <Text>{fmtMoney((price as any)?.pricePerMajorUnit)}</Text>
          );
        },
      },
      {
        title: "Minor",
        width: PP_COL_W.price,
        className: "pp-price-cell",
        render: (_: unknown, row: FeeFlatRow) => {
          const unitTypeName = getUnitTypeNameForFee(
              row.feeRow.entry.feeUnitTypeId,
              unitTypes,
              row.feeRow.unitTypeName
          );
          const rules = getUnitRules(row.feeRow.entry.feeUnitTypeId, unitTypeName);

          if (row.feeRow.offeringType === "One Off") {
            return renderRowSpanCell(row, <Text type="secondary">N/A</Text>);
          }

          if (!rules.allowsMinor) {
            return <Text type="secondary">N/A</Text>;
          }

          const editable = canEditRow(row.feeRow);
          const price = (row.period?.prices ?? []).find(
              (x: any) => String(x.currencyIsoCode).toUpperCase() === currencyIsoCode
          );

          return editable ? (
              renderWithSupplementEditOffset(
                  row,
                  editable,
                  <InputNumber
                      size="small"
                      min={0}
                      controls={false}
                      precision={2}
                      step={0.01}
                      value={Number(price?.pricePerMinorUnit ?? 0)}
                      onFocus={selectAllOnPriceFocus}
                      onChange={(v) =>
                          handleSupplementPeriodPriceChange(
                              row.feeRow,
                              Number(row.periodIndex ?? 0),
                              currencyIsoCode,
                              "pricePerMinorUnit",
                              typeof v === "number" ? v : null
                          )
                      }
                      style={FEE_PRICE_INPUT_STYLE}
                  />
              )
          ) : (
              <Text>{fmtMoney((price as any)?.pricePerMinorUnit)}</Text>
          );
        },
      },
    ],
  }));

      const columns = useMemo(() => {
        const getStatusEntries = (row: FeeRow) => {
          const statusMap = new Map<
              number,
              { id: number; name: string; status: SchoolOfferingStatus }
          >();

          (props.centreSchools ?? []).forEach((school) => {
            statusMap.set(school.id, {
              id: school.id,
              name: school.name,
              status: "Disabled",
            });
          });

          (row.entry.schoolStatuses ?? []).forEach((s: any, index: number) => {
            const id = getSchoolId(s);
            if (id == null) return;

            const current = statusMap.get(id);
            statusMap.set(id, {
              id,
              name: current?.name ?? getSchoolName(s, index + 1),
              status: (s?.status ?? "Disabled") as SchoolOfferingStatus,
            });
          });

          (row.entry.schoolIds ?? []).forEach((s: any, index: number) => {
            const id = getSchoolId(s);
            if (id == null) return;

            const current = statusMap.get(id);
            statusMap.set(id, {
              id,
              name: current?.name ?? getSchoolName(s, index + 1),
              status: current?.status ?? "Disabled",
            });
          });

          return [...statusMap.values()].sort((a, b) => a.name.localeCompare(b.name));
        };

        const rowHighlightColors = props.rowHighlightColors;
        const onRowHighlightChange = props.onRowHighlightChange;
        const highlightColumn =
          onRowHighlightChange && rowHighlightColors
            ? {
                title: "",
                dataIndex: "highlight",
                width: 44,
                align: "center" as const,
                render: (_: unknown, row: FeeFlatRow) => {
                  if (!row.isFirstPeriodRow)
                    return { children: null, props: { rowSpan: 0 } };
                  const feeId =
                    row.feeRow.entry.feeId?.id ?? row.feeRow.entry.feeId ?? "";
                  const key = `Fee-${feeId}`;
                  const color = rowHighlightColors[key] ?? "";
                  return {
                    children: (
                      <div className="pp-row-highlight-wrap">
                        <input
                          type="color"
                          className="pp-row-highlight-input"
                          value={color || "#e0e0e0"}
                          onChange={(e) =>
                            onRowHighlightChange(key, e.target.value || null)
                          }
                          title="Row highlight"
                        />
                        {color ? (
                          <Button
                            type="link"
                            size="small"
                            className="pp-row-highlight-clear"
                            onClick={() => onRowHighlightChange(key, null)}
                          >
                            Clear
                          </Button>
                        ) : null}
                      </div>
                    ),
                    props: { rowSpan: row.feeRowSpan },
                  };
                },
              }
            : null;

        return [
          ...(highlightColumn ? [highlightColumn] : []),
          {
            title: "",
            dataIndex: "actions",
            width: PP_COL_W.actions,
            align: "center" as const,
            render: (_: unknown, row: FeeFlatRow) => {
              if (!row.isFirstPeriodRow) return { children: null, props: { rowSpan: 0 } };

              const statuses = (row.feeRow.entry.schoolStatuses ?? []).map((s: any) => s?.status);
              const hasActive = statuses.includes("Active");
              const hasInactive = statuses.includes("Inactive");

              const items: any[] = [];
              if (hasActive) {
                items.push({
                  key: "delete",
                  label: "Delete",
                  onClick: () => {
                    void handleImmediateSchoolStatusAction(row.feeRow, "delete");
                  },
                });
              }

              if (hasInactive) {
                items.push({
                  key: "recover",
                  label: "Recover",
                  onClick: () => {
                    void handleImmediateSchoolStatusAction(row.feeRow, "recover");
                  },
                });
              }

              return renderRowSpanCell(
                  row,
                  <Dropdown menu={{ items }} trigger={["click"]}>
                    <Button
                        type="text"
                        icon={<BarsOutlined />}
                        aria-label="Fee actions"
                        disabled={row.feeRow.entry.hasPendingChanges || items.length === 0}
                    />
                  </Dropdown>
              );
            },
          },
          {
            title: "Offering Name",
            dataIndex: "name",
            width: PP_COL_W.feeName,
            render: (_: unknown, row: FeeFlatRow) =>
                renderRowSpanCell(
                    row,
                    <div className="product-name-cell">
                      <div className="product-name-header">
                        <div className="product-name-title">
                          {row.feeRow.entry.isNew ? (
                              <Tag color="green" style={{ marginBottom: 4 }}>
                                New
                              </Tag>
                          ) : null}
                          {!row.feeRow.entry.isNew && row.feeRow.entry.isUpdated ? (
                              <Tag color="blue" style={{ marginBottom: 4 }}>
                                Updated
                              </Tag>
                          ) : null}
                          <OpenProductLink
                            productId={row.feeRow.entry.feeId?.id}
                            productType="Fee"
                          >
                            {row.feeRow.entry.name}
                          </OpenProductLink>
                          {row.feeRow.entry.hasPendingChanges ? (
                              <LockOutlined style={{ marginLeft: 4 }} />
                          ) : null}
                        </div>
                        <div className="product-id-label">
                          ID: {row.feeRow.entry.feeId?.id ?? "—"}
                        </div>
                      </div>
                    </div>
                ),
          },
          {
            title: "Unit Type",
            dataIndex: "unitType",
            width: PP_COL_W.feeUnitType,
            render: (_: unknown, row: FeeFlatRow) =>
                renderRowSpanCell(
                    row,
                    <Text>
                      {unitTypes.find(
                              (u) => Number(u.id) === Number(row.feeRow.entry.feeUnitTypeId)
                          )?.name ??
                          row.feeRow.unitTypeName ??
                          "—"}
                    </Text>
                ),
          },
          {
            title: "Type",
            dataIndex: "offeringType",
            width: PP_COL_W.feeOfferingType,
            className: "pp-center-cell",
            render: (_: unknown, row: FeeFlatRow) =>
                renderRowSpanCell(
                    row,
                    <Tag color={row.feeRow.offeringType === "One Off" ? "green" : "geekblue"}>
                      {row.feeRow.offeringType}
                    </Tag>
                ),
          },
          {
            title: "Supplement Dates",
            children: [
              {
                title: "",
                width: PP_COL_W.actions,
                align: "center" as const,
                render: (_: unknown, row: FeeFlatRow) => {
                  if (row.feeRow.offeringType !== "Supplement") {
                    return renderRowSpanCell(
                        row,
                        <Text type="secondary">Not required</Text>,
                        { colSpan: 3 }
                    );
                  }

                  const checked = Boolean(row.feeRow.entry.supplementHasSpecificDates);

                  if (!checked) {
                    if (!row.isFirstPeriodRow) {
                      return { children: null, props: { rowSpan: 0 } };
                    }

                    return renderRowSpanCell(
                        row,
                        props.editMode ? (
                            <Checkbox
                                checked={checked}
                                onChange={(e) =>
                                    handleSpecificDatesToggle(row.feeRow, e.target.checked)
                                }
                            >
                              Set specific dates
                            </Checkbox>
                        ) : (
                            <Text type="secondary">All year</Text>
                        ),
                        { colSpan: 3 }
                    );
                  }

                  const items: any[] = [
                    {
                      key: "add-above",
                      label: "Add Row Above",
                      onClick: () =>
                          insertSupplementPeriodRelative(
                              row.feeRow,
                              Number(row.periodIndex ?? 0),
                              "above"
                          ),
                    },
                    {
                      key: "add-below",
                      label: "Add Row Below",
                      onClick: () =>
                          insertSupplementPeriodRelative(
                              row.feeRow,
                              Number(row.periodIndex ?? 0),
                              "below"
                          ),
                    },
                  ];

                  if ((row.feeRow.entry.supplementPeriods ?? []).length > 1) {
                    items.push({
                      key: "delete-row",
                      label: "Delete Row",
                      danger: true,
                      onClick: () =>
                          removeSupplementPeriod(row.feeRow, Number(row.periodIndex ?? 0)),
                    });
                  }

                  const burgerDisabled = !props.editMode || row.feeRow.entry.hasPendingChanges;

                  return props.editMode ? (
                      <Dropdown
                          menu={{ items }}
                          trigger={["click"]}
                          disabled={burgerDisabled}
                      >
                        <Button
                            type="text"
                            icon={<BarsOutlined />}
                            aria-label="Supplement period actions"
                            disabled={burgerDisabled}
                        />
                      </Dropdown>
                  ) : (
                      <div style={{ width: "100%", height: CHECKBOX_ROW_HEIGHT }} />
                  );
                },
              },
              {
                title: "From",
                width: PP_COL_W.date,
                className: "pp-date-cell",
                render: (_: unknown, row: FeeFlatRow) => {
                  if (row.feeRow.offeringType !== "Supplement") {
                    return { children: null, props: { colSpan: 0 } };
                  }

                  const editable = canEditRow(row.feeRow);
                  const checked = Boolean(row.feeRow.entry.supplementHasSpecificDates);

                  if (!checked) {
                    return { children: null, props: { colSpan: 0 } };
                  }

                  const period = row.period;
                  const startError = getFieldError(
                    `fee-${row.feeRow.key}-range-${row.periodIndex ?? 0}-start`
                  );

                  if (!editable) {
                    return <Text>{fmtDate(period?.startDate)}</Text>;
                  }

                  return (
                      <div style={FEE_LEFT_STACK_STYLE}>
                        {row.isFirstPeriodRow ? (
                            <Checkbox
                                checked={checked}
                                onChange={(e) =>
                                    handleSpecificDatesToggle(row.feeRow, e.target.checked)
                                }
                            >
                              Set specific dates
                            </Checkbox>
                        ) : null}

                        <DatePicker
                            components={DATE_PICKER_SELECT_ALL_COMPONENTS}
                            size="small"
                            value={period?.startDate ? dayjs(period.startDate as any) : null}
                            placeholder="Start"
                            format={DateHelper.DATE_PICKER_FORMATS}
                            status={startError ? "error" : undefined}
                            disabledDate={(date) => {
                              const maxForStart = period?.endDate
                                  ? dayjs(minIsoDate(String(period.endDate), supplementAllowedEndDate))
                                  : supplementAllowedEnd;

                              return (
                                  date.isBefore(supplementAllowedStart, "day") ||
                                  date.isAfter(maxForStart, "day")
                              );
                            }}
                            onChange={(d) =>
                                handleSupplementPeriodDateChange(
                                    row.feeRow,
                                    Number(row.periodIndex ?? 0),
                                    "startDate",
                                    d
                                )
                            }
                            style={FEE_DATE_INPUT_STYLE}
                        />
                      </div>
                  );
                },
              },
              {
                title: "To",
                width: PP_COL_W.date,
                className: "pp-date-cell",
                render: (_: unknown, row: FeeFlatRow) => {
                  if (row.feeRow.offeringType !== "Supplement") {
                    return { children: null, props: { colSpan: 0 } };
                  }

                  const editable = canEditRow(row.feeRow);
                  const checked = Boolean(row.feeRow.entry.supplementHasSpecificDates);

                  if (!checked) {
                    return { children: null, props: { colSpan: 0 } };
                  }

                  const period = row.period;
                  const endError = getFieldError(
                    `fee-${row.feeRow.key}-range-${row.periodIndex ?? 0}-end`
                  );

                  if (!editable) {
                    return <Text>{fmtDate(period?.endDate)}</Text>;
                  }

                  return (
                      <div style={FEE_LEFT_STACK_STYLE}>
                        {row.isFirstPeriodRow ? <CheckboxSpacer /> : null}

                        <DatePicker
                            components={DATE_PICKER_SELECT_ALL_COMPONENTS}
                            size="small"
                            value={period?.endDate ? dayjs(period.endDate as any) : null}
                            placeholder="End"
                            format={DateHelper.DATE_PICKER_FORMATS}
                            status={endError ? "error" : undefined}
                            disabledDate={(date) => {
                              const minForEnd = dayjs(
                                  maxIsoDate(String(period?.startDate ?? ""), supplementAllowedStartDate)
                              );

                              return (
                                  date.isBefore(minForEnd, "day") ||
                                  date.isAfter(supplementAllowedEnd, "day")
                              );
                            }}
                            onChange={(d) =>
                                handleSupplementPeriodDateChange(
                                    row.feeRow,
                                    Number(row.periodIndex ?? 0),
                                    "endDate",
                                    d
                                )
                            }
                            style={FEE_DATE_INPUT_STYLE}
                        />
                      </div>
                  );
                },
              },
            ],
          },
          ...priceColumns,
          {
            title: "Units",
            children: [
              {
                title: "Major",
                width: PP_COL_W.units,
                render: (_: unknown, row: FeeFlatRow) => {
                  if (row.feeRow.offeringType !== "Supplement") {
                    return renderRowSpanCell(
                        row,
                        <Text type="secondary">Not required</Text>,
                        { colSpan: 2 }
                    );
                  }

                  const max = row.feeRow.entry.supplementMaximumUnits;
                  const errMajor = getFieldError(`fee-${row.feeRow.key}-maxMajor`);
                  const editable = canEditRow(row.feeRow);

                  if (!editable) {
                    if (!max?.hasMaximum) {
                      return renderRowSpanCell(
                          row,
                          <Text type="secondary">No maximum</Text>,
                          { colSpan: 2 }
                      );
                    }

                    return renderRowSpanCell(
                        row,
                        <Text>{Number(max.majorUnits ?? 0)}</Text>
                    );
                  }

                  const checked = Boolean(max?.hasMaximum);

                  if (!checked) {
                    return renderRowSpanCell(
                        row,
                        <Space orientation="vertical" size="small" style={{ width: "100%" }}>
                          <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                  handleHasMaximumToggle(row.feeRow, e.target.checked)
                              }
                          >
                            Has maximum
                          </Checkbox>
                          <Text type="secondary">No maximum</Text>
                        </Space>,
                        { colSpan: 2 }
                    );
                  }

                  return renderRowSpanCell(
                      row,
                      <div style={FEE_UNITS_STACK_STYLE}>
                        <div style={MAX_UNITS_CHECKBOX_ROW_STYLE}>
                          <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                  handleHasMaximumToggle(row.feeRow, e.target.checked)
                              }
                          >
                            Has maximum
                          </Checkbox>
                        </div>

                        <InputNumber
                            size="small"
                            min={0}
                            placeholder="Major"
                            value={Number(max?.majorUnits ?? 0)}
                            controls={false}
                            status={errMajor ? "error" : undefined}
                            onChange={(v) =>
                                handleMaximumUnitsChange(
                                    row.feeRow,
                                    "majorUnits",
                                    typeof v === "number" ? v : null
                                )
                            }
                            style={FEE_SMALL_INPUT_STYLE}
                        />

                        {errMajor ? (
                            <Text type="danger" style={{ fontSize: 12 }}>
                              {errMajor}
                            </Text>
                        ) : null}
                      </div>
                  );
                },
              },
              {
                title: "Minor",
                width: PP_COL_W.units,
                render: (_: unknown, row: FeeFlatRow) => {
                  if (row.feeRow.offeringType !== "Supplement") {
                    return { children: null, props: { rowSpan: 0, colSpan: 0 } };
                  }

                  const unitTypeName = getUnitTypeNameForFee(
                      row.feeRow.entry.feeUnitTypeId,
                      unitTypes,
                      row.feeRow.unitTypeName
                  );
                  const rules = getUnitRules(row.feeRow.entry.feeUnitTypeId, unitTypeName);
                  const max = row.feeRow.entry.supplementMaximumUnits;
                  const editable = canEditRow(row.feeRow);

                  if (!max?.hasMaximum) {
                    return { children: null, props: { rowSpan: 0, colSpan: 0 } };
                  }

                  if (!rules.allowsMinor) {
                    return renderRowSpanCell(row, <Text type="secondary">N/A</Text>);
                  }

                  if (!editable) {
                    return renderRowSpanCell(
                        row,
                        <Text>{Number(max?.minorUnits ?? 0)}</Text>
                    );
                  }

                  return renderRowSpanCell(
                      row,
                      <div style={FEE_UNITS_STACK_STYLE}>
                        <div style={MAX_UNITS_SPACER_STYLE} />
                        <InputNumber
                            size="small"
                            min={0}
                            max={rules.minorMax}
                            placeholder="Minor"
                            value={Number(max?.minorUnits ?? 0)}
                            controls={false}
                            onChange={(v) =>
                                handleMaximumUnitsChange(
                                    row.feeRow,
                                    "minorUnits",
                                    typeof v === "number" ? v : null
                                )
                            }
                            style={FEE_SMALL_INPUT_STYLE}
                        />
                      </div>
                  );
                },
              },
            ],
          },
          {
            title: "Age range",
            children: [
              {
                title: "Min",
                width: PP_COL_W.age,
                className: "pp-age-cell",
                render: (_: unknown, row: FeeFlatRow) => {
                  const editable = canEditRow(row.feeRow);
                  const minAgeError = getFieldError(`fee-${row.feeRow.key}-minAge`);
                  const value =
                      row.feeRow.entry.minimumAge == null
                          ? null
                          : Number(row.feeRow.entry.minimumAge);

                  if (!editable) {
                    return renderRowSpanCell(
                        row,
                        <Text>{value == null ? "—" : value}</Text>
                    );
                  }

                  return renderRowSpanCell(
                      row,
                      renderWithSupplementEditOffset(
                          row,
                          editable,
                          <InputNumber
                              size="small"
                              min={0}
                              precision={0}
                              placeholder="Min"
                              value={value}
                              controls={false}
                              status={minAgeError ? "error" : undefined}
                              onChange={(v) =>
                                  handleAgeChange(
                                      row.feeRow,
                                      "minimumAge",
                                      typeof v === "number" ? v : null
                                  )
                              }
                              style={FEE_SMALL_INPUT_STYLE}
                          />
                      )
                  );
                },
              },
              {
                title: "Max",
                width: PP_COL_W.age,
                className: "pp-age-cell",
                render: (_: unknown, row: FeeFlatRow) => {
                  const editable = canEditRow(row.feeRow);
                  const err = getFieldError(`fee-${row.feeRow.key}-maxAge`);
                  const value =
                      row.feeRow.entry.maximumAge == null
                          ? null
                          : Number(row.feeRow.entry.maximumAge);

                  if (!editable) {
                    return renderRowSpanCell(
                        row,
                        <Text>{value == null ? "—" : value}</Text>
                    );
                  }

                  return renderRowSpanCell(
                      row,
                      renderWithSupplementEditOffset(
                          row,
                          editable,
                          <Space orientation="vertical" size="small" style={{ width: "100%" }}>
                            <InputNumber
                                size="small"
                                min={0}
                                precision={0}
                                placeholder="Max"
                                value={value}
                                controls={false}
                                status={err ? "error" : undefined}
                                onChange={(v) =>
                                    handleAgeChange(
                                        row.feeRow,
                                        "maximumAge",
                                        typeof v === "number" ? v : null
                                    )
                                }
                                style={FEE_SMALL_INPUT_STYLE}
                            />
                            {err ? (
                                <Text type="danger" style={{ fontSize: 12 }}>
                                  {err}
                                </Text>
                            ) : null}
                          </Space>
                      )
                  );
                },
              },
            ],
          },
          {
            title: "Schools",
            dataIndex: "schools",
            width: PP_COL_W.schools,
            render: (_: unknown, row: FeeFlatRow) => {
              const statusEntries = getStatusEntries(row.feeRow);
              const schoolsError = getFieldError(`fee-${row.feeRow.key}-schools`);
              const selectedIds = (row.feeRow.entry.schoolIds ?? [])
                  .map((x: any) => Number((x as any)?.id ?? x))
                  .filter((x: number) => Number.isFinite(x));

              if (props.editMode && !row.feeRow.entry.hasPendingChanges) {
                const content = (
                    <Checkbox.Group
                        value={selectedIds}
                        onChange={(values) =>
                            updateFeeEntry(row.feeRow, (draft) => {
                              const ids = (values as Array<number | string>)
                                  .map((id) => Number(id))
                                  .filter((id) => Number.isFinite(id));
                              draft.schoolIds = ids.map((id) => ({ id })) as any;
                            })
                        }
                    >
                      <Space orientation="vertical" size={6}>
                        {statusEntries.map((entry) => (
                            <Checkbox key={`${row.key}-school-${entry.id}`} value={entry.id}>
                              <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                              >
                                <span>{entry.name}</span>
                                {renderSchoolStatusTag(entry.status)}
                              </span>
                            </Checkbox>
                        ))}
                      </Space>
                    </Checkbox.Group>
                );

                const child =
                    statusEntries.length > 6 ? (
                        <Popover content={content} trigger="click" placement="bottomLeft">
                          <Button
                            size="small"
                            danger={!!schoolsError}
                          >
                            {selectedIds.length} selected
                          </Button>
                        </Popover>
                    ) : (
                        content
                    );

                return renderRowSpanCell(
                    row,
                    <Space orientation="vertical" size={6} style={{ width: "100%" }}>
                      <div style={feeFieldErrorOutline(!!schoolsError)}>{child}</div>
                      {schoolsError ? (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            {schoolsError}
                          </Text>
                      ) : null}
                    </Space>
                );
              }

              const compactTooltip = statusEntries
                  .map((entry) => `${entry.name} — ${entry.status}`)
                  .join("\\n");

              const activeOrInactive = statusEntries.filter(
                  (entry) => entry.status !== "Disabled"
              );

              const viewChild =
                  statusEntries.length > 3 ? (
                      <Tooltip title={compactTooltip}>
          <span>
            {activeOrInactive[0]?.name ?? statusEntries[0]?.name ?? "School"}
            {statusEntries.length > 1 ? (
                <Text type="secondary" style={{ marginLeft: 6 }}>
                  (+{statusEntries.length - 1} more)
                </Text>
            ) : null}
          </span>
                      </Tooltip>
                  ) : (
                      <Space orientation="vertical" size={4}>
                        {statusEntries.map((entry) => (
                            <div
                                className="school-line"
                                key={`${row.key}-school-view-${entry.id}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 8,
                                }}
                            >
                              <Text>{entry.name}</Text>
                              {renderSchoolStatusTag(entry.status)}
                            </div>
                        ))}
                      </Space>
                  );

              return renderRowSpanCell(row, viewChild);
            },
          },
          {
            title: "Applicable Products",
            dataIndex: "applicableProducts",
            width: PP_COL_W.applicable,
            render: (_: unknown, row: FeeFlatRow) => {
              const applicableError = getFieldError(
                `fee-${row.feeRow.key}-applicableProducts`
              );

              return renderRowSpanCell(
                row,
                <Space orientation="vertical" size={4}>
                  <Button
                    type="link"
                    onClick={() => openApplicableProductsModal(row.feeRow)}
                    disabled={!props.editMode || row.feeRow.entry.hasPendingChanges}
                    danger={!!applicableError}
                  >
                    View/Edit
                  </Button>
                  {applicableError ? (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {applicableError}
                    </Text>
                  ) : null}
                </Space>
              );
            },
          },
        ];
      }, [
        props.editMode,
        props.centreSchools,
        props.rowHighlightColors,
        props.onRowHighlightChange,
        unitTypes,
        selectedYear,
        canEditRow,
        getFieldError,
        handleSpecificDatesToggle,
        handleSupplementPeriodDateChange,
        handleHasMaximumToggle,
        handleMaximumUnitsChange,
        handleSupplementPeriodPriceChange,
        insertSupplementPeriodRelative,
        removeSupplementPeriod,
        openApplicableProductsModal,
        updateFeeEntry,
        enabledCurrencies,
        renderRowSpanCell,
        handleImmediateSchoolStatusAction,
        priceColumns,
        handleAgeChange,
      ]);

      const hasCentre = Boolean(selectedCentreCode);
      const hasFees = feeRows.length > 0;

    return (
      <>
        {!hasCentre ? (
          <Alert
            title="Select a centre"
            description="Please select a centre to load fee offerings."
            type="info"
            showIcon
          />
        ) : (
            <>
              {!hasFees && !isLoading ? (
                  <Alert
                      title="No fee offerings found"
                      type="info"
                      showIcon
                      style={{ marginTop: 16 }}
                  />
              ) : null}

          <Table
            className="pp-fee-table"
            columns={columns}
            rowClassName={feeRowClassName}
            dataSource={hasFees ? flatRows : []}
            rowKey="key"
            pagination={false}
            bordered
            size="small"
            tableLayout="fixed"
            scroll={{ x: "max-content" }}
            sticky
            loading={isLoading}
            onRow={(row: FeeFlatRow) => feeRowEvents(row)}
              />
          </>
        )}

        <Modal
          title={
            appProductsModal.row
              ? `"${appProductsModal.row.entry.name}" - Applicable Products`
              : "Applicable Products"
          }
          open={appProductsModal.visible}
          onCancel={closeApplicableProductsModal}
          onOk={handleApplicableSave}
          okText="Apply"
          okButtonProps={{ disabled: Boolean(applicableSaveError) }}
          destroyOnHidden
          width={760}
        >
          {appProductsModal.row ? (
            <Space orientation="vertical" style={{ width: "100%" }} size="large">
              {showApplicableEmptyError ? (
                  <Alert type="error" showIcon title={applicableSaveError} />
              ) : null}
              <div>
                <Text strong>Product Type</Text>
                <Select
                  value={appProductsModal.selectedProductType}
                  placeholder={loadingProducts ? "Loading..." : "Select a product type"}
                  style={{ width: "100%", marginTop: 4 }}
                  loading={loadingProducts}
                  disabled={loadingProducts}
                  onChange={(value) => {
                    const nextType = value as ProductTypeKey;
                    const nextTypeId = PRODUCT_TYPE_NAME_TO_ID[nextType];

                    setAppProductsModal((prev) => {
                      const existing = prev.items.find(
                        (x) => Number(x.productTypeId) === nextTypeId
                      );

                      const inferredTarget = existing
                        ? inferTargetFromItem(existing)
                        : DEFAULT_TARGET_FOR_GROUPED;
                      const nextTarget: ApplicableTarget = supportsTypesAndProducts(nextType)
                          ? inferredTarget
                          : "Products";

                      return {
                        ...prev,
                        selectedProductType: nextType,
                        editingTypeId: nextTypeId,
                        selectionTarget: supportsTypesAndProducts(nextType)
                          ? nextTarget
                          : "Products",
                        selectionMode: existing
                          ? normalizeSelectionMode(existing.selectionMode)
                          : prev.selectionMode ?? DEFAULT_SELECTION_MODE,
                        selectedProductIds: existing
                          ? getApplicableIds(existing, nextTarget).map(String)
                          : [],
                        error: undefined,
                      };
                    });
                  }}
                  options={productTypeOptions}
                  notFoundContent={
                    loadingProducts ? (
                      <Spin size="small" />
                    ) : (
                      <Text type="secondary">No product types available</Text>
                    )
                  }
                />
              </div>

              {supportsTypesAndProducts(appProductsModal.selectedProductType) ? (
                <div>
                  <Text strong>Select by</Text>
                  <Radio.Group
                    style={{ display: "block", marginTop: 4 }}
                    value={
                      appProductsModal.selectionTarget ?? DEFAULT_TARGET_FOR_GROUPED
                    }
                    onChange={(e) =>
                      setAppProductsModal((prev) => ({
                        ...prev,
                        selectionTarget: e.target.value as ApplicableTarget,
                        selectedProductIds: [],
                        error: undefined,
                      }))
                    }
                    disabled={
                      appProductsModal.selectionMode === "SpecificProductType"
                    }
                  >
                    <Radio.Button value="Types">
                      {appProductsModal.selectedProductType === "Accommodation"
                        ? "Accommodation Types"
                        : "AddOn Types"}
                    </Radio.Button>
                    <Radio.Button value="Products">
                      {appProductsModal.selectedProductType === "Accommodation"
                        ? "Accommodation Products"
                        : "AddOn Products"}
                    </Radio.Button>
                  </Radio.Group>
                </div>
              ) : null}

              <div>
                <Text strong>Selection Type</Text>
                <Radio.Group
                  style={{ display: "block", marginTop: 4 }}
                  value={appProductsModal.selectionMode}
                  onChange={(event) =>
                    setAppProductsModal((prev) => ({
                      ...prev,
                      selectionMode: event.target.value as ApplicableSelectionMode,
                      selectedProductIds: [],
                      error: undefined,
                    }))
                  }
                >
                  {SELECTION_MODES.map((mode) => (
                    <Radio key={mode} style={{ marginBottom: 4 }} value={mode}>
                      {SELECTION_MODE_LABELS[mode]}
                    </Radio>
                  ))}
                </Radio.Group>
              </div>

              {appProductsModal.row.offeringType === "One Off" ? (
                  <Space size={16} wrap>
                    <div>
                      <Text strong>Min Duration</Text>
                      <InputNumber
                          min={0}
                          precision={0}
                          value={appProductsModal.minDuration as any}
                          onChange={(v) =>
                              setAppProductsModal((prev) => ({
                                ...prev,
                                minDuration: typeof v === "number" ? v : null,
                                error: undefined,
                              }))
                          }
                          style={{ width: 140, display: "block", marginTop: 4 }}
                      />
                    </div>
                    <div>
                      <Text strong>Max Duration</Text>
                      <InputNumber
                          min={0}
                          precision={0}
                          value={appProductsModal.maxDuration as any}
                          onChange={(v) =>
                              setAppProductsModal((prev) => ({
                                ...prev,
                                maxDuration: typeof v === "number" ? v : null,
                                error: undefined,
                              }))
                          }
                          style={{ width: 140, display: "block", marginTop: 4 }}
                      />
                    </div>
                  </Space>
              ) : null}

              {appProductsModal.selectionMode !== "SpecificProductType" &&
              appProductsModal.selectedProductType
                ? (() => {
                  const pt = appProductsModal.selectedProductType;
                  const target: ApplicableTarget = supportsTypesAndProducts(pt)
                      ? appProductsModal.selectionTarget ?? DEFAULT_TARGET_FOR_GROUPED
                      : "Products";

                    const isTypesTarget = target === "Types";
                    const chooseLabel = isTypesTarget
                      ? "Choose Types"
                      : "Choose Products";
                    const placeholder = isTypesTarget
                      ? "Select a type"
                      : "Select products";

                    const optionsKey = optionKey(pt, target);
                    const options = productOptionsByKey[optionsKey] ?? [];

                    return (
                      <div>
                        <Text strong>{chooseLabel}</Text>
                        <Select
                          key={optionsKey}
                          {...(!isTypesTarget ? { mode: "multiple" as const } : {})}
                          allowClear
                          showSearch={{ optionFilterProp: 'label' }}
                          placeholder={placeholder}
                          style={{ width: "100%", marginTop: 4 }}
                          loading={loadingProducts}
                          value={
                            isTypesTarget
                              ? appProductsModal.selectedProductIds[0] ?? undefined
                              : appProductsModal.selectedProductIds
                          }
                          onChange={(value) => {
                            setAppProductsModal((prev) => {
                              const pt2 = prev.selectedProductType!;
                              const target2: ApplicableTarget =
                                supportsTypesAndProducts(pt2)
                                  ? prev.selectionTarget ?? DEFAULT_TARGET_FOR_GROUPED
                                  : "Products";

                              if (target2 !== "Types") {
                                const arr = Array.isArray(value) ? value : [];
                                return {
                                  ...prev,
                                  selectedProductIds: arr.map(String),
                                  error: undefined,
                                };
                              }

                              const v = value == null ? undefined : String(value);
                              return {
                                ...prev,
                                selectedProductIds: v ? [v] : [],
                                error: undefined,
                              };
                            });
                          }}
                          options={options}
                          notFoundContent={
                            loadingProducts ? (
                              <Spin size="small" />
                            ) : (
                              <Text type="secondary">No data</Text>
                            )
                          }
                        />
                      </div>
                    );
                  })()
                : null}

              {appProductsModal.error ? (
                <Text type="danger">{appProductsModal.error}</Text>
              ) : null}

              <Space>
                <Button
                  type="primary"
                  icon={
                    appProductsModal.editingTypeId == null ? (
                      <PlusOutlined />
                    ) : undefined
                  }
                  onClick={handleApplicableAddOrUpdate}
                >
                  {appProductsModal.editingTypeId != null ? "Update" : "Add"}
                </Button>
                <Button
                  onClick={() =>
                    setAppProductsModal((prev) => ({
                      ...prev,
                      selectedProductType: undefined,
                      selectionMode: undefined,
                      selectionTarget: undefined,
                      selectedProductIds: [],
                      editingTypeId: null,
                      error: undefined,
                    }))
                  }
                >
                  Clear
                </Button>
              </Space>

              <Divider
                titlePlacement="start"
                plain
                style={{ marginTop: 8, marginBottom: 12 }}
              >
                <Space size={8}>
                  <Text strong>Configured Rules</Text>
                  <Tag>{appProductsModal.items.length}</Tag>
                </Space>
              </Divider>

              {appProductsModal.items.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    background: token.colorFillAlter,
                  }}
                >
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No rules configured yet"
                  />
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[...appProductsModal.items]
                    .sort(
                      (a, b) => Number(a.productTypeId) - Number(b.productTypeId)
                    )
                    .map((item) => {
                      const rawPtId = Number((item as any)?.productTypeId ?? 0);
                      const rawPtName = String(
                        (item as any)?.productTypeName ??
                          (item as any)?.productType ??
                          ""
                      ).trim();
                      const normalizedPtName = rawPtName
                        .toLowerCase()
                        .replace(/[^a-z]/g, "");
                      const inferredPtId =
                        rawPtId ||
                        (normalizedPtName === "course" || normalizedPtName === "courses"
                          ? PRODUCT_TYPE_NAME_TO_ID.Course
                          : normalizedPtName === "accommodation" ||
                              normalizedPtName === "accommodations"
                            ? PRODUCT_TYPE_NAME_TO_ID.Accommodation
                            : normalizedPtName === "addon" ||
                                normalizedPtName === "addons" ||
                                normalizedPtName === "addonso" ||
                                normalizedPtName === "addonsproduct"
                              ? PRODUCT_TYPE_NAME_TO_ID.AddOn
                              : normalizedPtName === "transfer" ||
                                  normalizedPtName === "transfers"
                                ? PRODUCT_TYPE_NAME_TO_ID.Transfer
                                : normalizedPtName === "package" ||
                                    normalizedPtName === "packages"
                                  ? PRODUCT_TYPE_NAME_TO_ID.Package
                                  : 0);

                      const pt = PRODUCT_TYPE_ID_TO_NAME[Number(inferredPtId ?? 0)];
                      const mode = normalizeSelectionMode(item.selectionMode);
                      const inferredTarget = inferTargetFromItem(item);
                      const count = getApplicableIds(item, inferredTarget).length;
                      const title = pt ? PRODUCT_TYPE_LABELS[pt] : "Unknown";
                      const targetLabel = supportsTypesAndProducts(pt)
                            ? inferredTarget === "Types"
                          ? "Types"
                          : "Products"
                              : undefined;

                      return (
                          <div
                              key={String(inferredPtId || rawPtName || item.productTypeId)}
                              style={{
                                      borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                      paddingBottom: 12,
                                    }}
                                >
                                  <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        alignItems: "flex-start",
                                      }}
                                  >
                                    <div style={{ minWidth: 0 }}>
                                      <Space wrap size={8}>
                                        <Text strong>{title}</Text>
                                        {targetLabel ? <Tag>{targetLabel}</Tag> : null}
                                        <Tag
                                            color={
                                              mode === "SpecificProductType"
                                                  ? "green"
                                                  : mode === "IncludeSelected"
                                                      ? "blue"
                                                      : "red"
                                            }
                                        >
                                          {mode === "SpecificProductType"
                                              ? "All"
                                              : mode === "IncludeSelected"
                                                  ? "Include"
                                                  : "Exclude"}
                                        </Tag>
                                        {mode !== "SpecificProductType" ? <Tag>{count}</Tag> : null}
                                      </Space>

                                      <Text
                                          type="secondary"
                                          style={{ display: "block", marginTop: 6 }}
                                      >
                                        {mode === "SpecificProductType"
                                            ? "Applies to all products of this type"
                                            : `Selected: ${count}`}
                                      </Text>

                                      {/*{appProductsModal.row?.offeringType === "One Off" &&*/}
                                      {/*(item.minDuration != null || item.maxDuration != null) ? (*/}
                                      {/*    <Text*/}
                                      {/*        type="secondary"*/}
                                      {/*        style={{ display: "block", marginTop: 4 }}*/}
                                      {/*    >*/}
                                      {/*      Duration: {item.minDuration ?? "—"} - {item.maxDuration ?? "—"}*/}
                                      {/*    </Text>*/}
                                      {/*) : null}*/}
                                    </div>

                                    <Space size={8}>
                                      <Button
                                          size="small"
                                          icon={<EditOutlined />}
                                          onClick={() =>
                                              handleApplicableEdit(Number(inferredPtId))
                                          }
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                          size="small"
                                          danger
                                          icon={<DeleteOutlined />}
                                          onClick={() =>
                                              handleApplicableDelete(Number(inferredPtId))
                                          }
                                      >
                                        Delete
                                      </Button>
                                    </Space>
                                  </div>
                                </div>
                            );
                          })}
                    </div>
                )}
              </Space>
          ) : null}
        </Modal>

        <Modal
          title="Add Fee"
          open={addModalVisible}
          onCancel={() => {
            setAddModalVisible(false);
            setSelectedFeeId(undefined);
          }}
          onOk={handleAddFee}
          okText="Add"
          okButtonProps={{ disabled: !selectedFeeId || addFeeLoading }}
          destroyOnHidden
          width={600}
        >
          <Space orientation="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Text strong>Select Fee Product</Text>
              <Select
                value={selectedFeeId}
                placeholder="Select a fee product to add"
                style={{ width: "100%", marginTop: 8 }}
                loading={addFeeLoading}
                showSearch={{ optionFilterProp: 'label' }}
                onChange={(value) => setSelectedFeeId(Number(value))}
                options={availableFees.map((fee) => ({
                  label: `${fee.name}${fee.unitTypeName ? ` (${fee.unitTypeName})` : ""}`,
                  value: fee.id,
                }))}
                notFoundContent={
                  addFeeLoading ? (
                    <Spin size="small" />
                  ) : (
                    <Text type="secondary">No available fees</Text>
                  )
                }
              />
              {availableFees.length === 0 && !addFeeLoading ? (
                <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                  All fee products are already in this pricelist, or no active fees are available.
                </Text>
              ) : null}
            </div>
          </Space>
        </Modal>
      </>
  );
}
);

FeePricelistTable.displayName = "FeePricelistTable";

export default FeePricelistTable;